// Strings for ThervuMitra's own UI (header search, catalog home, course cards,
// sign-in layout, dashboard card, footer).
// Open edX's own screens use the platform translations.
import { getConfig } from '@edx/frontend-platform';
import { getLocale } from '@edx/frontend-platform/i18n';

import { ALL_LANGUAGES, RTL_LANGUAGES } from './languages';
// Machine-translated ThervuMitra strings for the other Indian languages,
// generated by build/translations (IndicTrans2). Human strings below win.
import GENERATED from './messages.generated';

const MESSAGES = {
  en: {
    heroTitle: 'ஆசிரியர் கனவை',
    heroTitleHighlight: 'நனவாக்குவோம்.',
    heroSubtitle: 'Prepare for PG TRB, TN TET, UGC NET and every Tamil Nadu teacher recruitment exam: courses in Tamil and English, timed mock tests with ranks, and Vidya, your AI tutor.',
    heroPointPace: 'Learn at your own pace',
    heroPointCertificate: 'A study plan for your exam date',
    heroPointLanguages: 'Tamil and English medium',
    heroPointPath: 'Timed mock tests with ranks',
    heroWatchVideo: 'Watch video',
    heroVideoTitle: 'ThervuMitra in one minute',
    heroCloseVideo: 'Close video',
    heroPointTutor: 'Vidya AI tutor, in Tamil and English',
    getStarted: 'Get started',
    searchPlaceholder: 'What do you want to learn?',
    searchButton: 'Search courses',
    search: 'Search',
    statCourses: 'Courses',
    statPartners: 'Partner institutions',
    statAnytime: 'Learn anytime',
    popularTitle: 'Most popular courses',
    popularSubtitle: 'Start with a course from our partner institutions.',
    viewAll: 'View all courses',
    viewCourse: 'View course',
    tabAll: 'All',
    tabNoCourses: 'No courses in this category yet.',
    homeCoursesError: 'Courses could not be loaded. Please try again later.',
    trendingTitle: 'Our Trending Courses',
    trendingSubtitle: 'Fresh courses learners are starting right now.',
    home: 'Home',
    catalogTitle: 'Explore courses',
    catalogSubtitle: 'Courses for every Tamil Nadu teacher recruitment exam, in Tamil and English.',
    // Course list page (thervumitra/catalog/CoursesPage.jsx).
    crHeroTitle: 'Which exam are you preparing for?',
    crHeroText: 'Courses, mock tests and an AI tutor for PG TRB, TN TET, UGC NET and more, in Tamil and English.',
    crSearchLabel: 'Search courses',
    crSearchPlaceholder: 'Search courses or subjects',
    crStatCourses: '{count} courses',
    crStatCategories: '{count} exams',
    crStatFree: '{count} free courses',
    crCategoriesTitle: 'Choose your exam',
    crCategoriesText: 'Pick an exam to see its courses, or browse every course below.',
    crCategoryCount: '{count} courses',
    crCategoryCountOne: '1 course',
    crAllCourses: 'All courses',
    crAll: 'All',
    crFilters: 'Filters',
    crClearAll: 'Clear all',
    crShowing: 'Showing {shown} of {total} courses',
    crSortLabel: 'Sort by',
    crSortNewest: 'Newest',
    crSortTitle: 'Title (A to Z)',
    crSortPriceLow: 'Price: low to high',
    crSortPriceHigh: 'Price: high to low',
    crLoadMore: 'Show more courses',
    crNoResults: 'No courses match your search.',
    crNoResultsText: 'Try other words, or clear the filters to see every course.',
    crShowResults: 'Show {count} courses',
    crCloseFilters: 'Close filters',
    crRemoveFilter: 'Remove filter: {name}',
    crPathTitle: 'Not sure where to start?',
    crPathText: 'Tell us your goal and AI plans which courses to take, in what order.',
    authTitle: 'Welcome to our learning community',
    authSubtitle: 'One account for every ThervuMitra course, on any device.',
    authPoint: 'Let us learn something new today!',
    welcome: 'Welcome to ThervuMitra, {name}.',
    welcomeAnonymous: 'Welcome to ThervuMitra.',
    explore: 'Explore courses',
    dashHello: 'Hello, {name}!',
    dashText: 'Pick up where you left off, or find something new to learn.',
    dashProfile: 'View profile',
    dashAccount: 'Account settings',
    dashExploreTitle: 'Find your next course',
    dashExploreText: 'Browse open courses from partner institutions.',
    language: 'Language',
    chooseLanguage: 'Choose your language',
    footerLine: 'TRB தேர்வுக்கு உங்கள் நண்பன் · Your partner for every TRB exam.',
    footerCompany: 'Company',
    footerLearn: 'Learn',
    footerLegal: 'Legal',
    footerContact: 'Contact us',
    myDashboard: 'My dashboard',
    about: 'About',
    terms: 'Terms of service',
    privacy: 'Privacy policy',
    initiative: 'A Gheer Technologies initiative.',
    contentTranslated: 'This unit is machine-translated into {language}.',
    contentOriginal: 'Showing the original. This unit is also available in {language}.',
    contentTranslatedNote: 'Automatic translation can contain mistakes.',
    showOriginal: 'Show original',
    showTranslated: 'Show in {language}',
    blog: 'Blog',
    blogTitle: 'ThervuMitra blog',
    blogSubtitle: 'News, stories and learning tips from the ThervuMitra team.',
    readMore: 'Read more',
    noPosts: 'No posts yet. Please check back soon.',
    backToBlog: 'Back to the blog',
    newerPosts: 'Newer posts',
    olderPosts: 'Older posts',
    pageOf: 'Page {page} of {pages}',
    loading: 'Loading…',
    notFound: 'Page not found',
    notFoundText: 'The page you are looking for does not exist or has moved.',
    onThisPage: 'On this page',
    lastUpdated: 'Last updated: {date}',
    legalTranslatedNote: 'This page was translated automatically. If anything differs, the English version applies.',
    pageTranslatedNote: 'This page was translated automatically and may contain mistakes.',
    pageOriginalNote: 'You are reading the original English version.',
    translationInProgress: 'Some parts are still being translated.',
    readInEnglish: 'Read in English',
    readTranslated: 'Read in my language',
    contactTitle: 'Contact us',
    contactHeading: 'We are here to help',
    contactIntro: 'Whether you have a question about a course, want to bring ThervuMitra to your institution, or have feedback for us, we would love to hear from you.',
    contactPromise: 'Leave your details and our team will reply within two working days.',
    topicLabel: 'What are you contacting us about?',
    topic_course: 'Course enquiry or enrolling in a course',
    topic_feedback: 'Feedback',
    topic_teams: 'Training for teams',
    topic_partner: 'Partnership or business enquiry',
    topic_academia: 'Academic institution enquiry',
    topic_other: 'Other',
    courseLabel: 'Which course are you interested in?',
    nameLabel: 'Full name',
    emailLabel: 'Email',
    countryLabel: 'Country',
    phoneLabel: 'Phone number (optional)',
    codeLabel: 'Country code',
    messageLabel: 'Message',
    consentLabel: 'Send me news about courses and events from ThervuMitra.',
    agreeNote: 'By sending this form you agree to our {privacy} and {terms}.',
    contactError: 'Something went wrong. Please try again, or write to us at {email}.',
    required: 'This field is required.',
    invalidEmail: 'Please enter a valid email address.',
    sending: 'Sending…',
    sendMessage: 'Send message',
    contactThanks: 'Thank you! We have received your message and will get back to you soon.',
    whyTitle: 'Why learn with ThervuMitra',
    benefitLanguageTitle: 'Tamil and English',
    benefitLanguageText: 'Study in Tamil or English: switch the site language any time, and course units can be machine-translated into Tamil.',
    benefitPaceTitle: 'Learn at your own pace',
    benefitPaceText: 'Study anytime, on any device, at a pace that suits you.',
    benefitPartnersTitle: 'Courses from trusted institutions',
    benefitPartnersText: 'Courses designed and taught by our partner institutions.',
    benefitCertificateTitle: 'Earn certificates',
    benefitCertificateText: 'Show what you have learned with a certificate you can share.',
    benefitOpenTitle: 'Open to everyone',
    benefitOpenText: 'Create an account and start learning in minutes.',
    enquiries: 'Enquiries',
    generalQueries: 'General queries',
    newsletterTitle: 'Newsletter',
    newsletterText: 'New courses, learning tips and ThervuMitra news in your inbox.',
    subscribe: 'Subscribe',
    newsletterThanks: 'Almost done! Please check your inbox and confirm your subscription.',
    newsletterError: 'We could not subscribe you just now. Please try again later.',
    newsletterConfirmed: 'You are subscribed',
    newsletterConfirmedText: 'Thank you for confirming. Our next newsletter will reach your inbox.',
    newsletterUnsubscribed: 'You have unsubscribed',
    newsletterUnsubscribedText: 'You will not receive our newsletter any more. You can subscribe again at any time from the bottom of any page.',
    newsletterInvalid: 'This link does not work',
    newsletterInvalidText: 'The link may be incomplete or out of date. Please subscribe again from the bottom of any page.',
    assistantTitle: 'ThervuMitra assistant',
    assistantOpen: 'Ask ThervuMitra',
    assistantClose: 'Close chat',
    assistantNew: 'New chat',
    assistantGreeting: 'Hello! I can help you find courses and answer questions about ThervuMitra. What would you like to learn?',
    assistantPlaceholder: 'Type your question…',
    assistantSend: 'Send',
    assistantThinking: 'Thinking…',
    assistantSearching: 'Looking that up…',
    assistantDisclaimer: 'AI answers can be wrong. Check important details on the course page.',
    assistantLimit: 'You have asked a lot of questions in a short time. Please try again a little later.',
    assistantBudget: 'The assistant is resting for today. Please browse the course list or contact us.',
    assistantUnavailable: 'The assistant is not available right now. Please try again later or contact us.',
    assistantSuggest1: 'Which courses can I take?',
    assistantSuggest2: 'How do I change the language?',
    assistantSuggest3: 'Do courses give certificates?',
    assistantSuggest4: 'How can I contact you?',
    tutorTitle: 'AI Tutor',
    tutorIntro: 'Ask me anything about {course}. I answer from this course\'s materials.',
    tutorGreeting: 'I am your tutor for this course. Ask me to explain a topic, summarise a unit, or help you plan your study.',
    tutorPlaceholder: 'Ask about this course…',
    tutorLoading: 'Getting your tutor ready…',
    tutorUnavailable: 'The AI Tutor is not available right now. Please try again in a little while.',
    tutorNotAllowed: 'The AI Tutor is available to learners enrolled in this course.',
    tutorBudget: 'The AI Tutor is resting for today. Please try again tomorrow.',
    tutorReading: 'Reading the course…',
    tutorCheckingProgress: 'Checking your progress…',
    tutorDisclaimer: 'AI answers can be wrong. Check the course material for anything important.',
    tutorHelpTitle: 'What I can help with',
    tutorHelp1: 'Explain ideas from the course in simple words',
    tutorHelp2: 'Find where a topic is covered in the course',
    tutorHelp3: 'Summarise a unit or a section',
    tutorHelp4: 'Check your progress and upcoming deadlines',
    tutorTryTitle: 'Try asking',
    tutorSuggest1: 'What are the main ideas of this course?',
    tutorSuggest2: 'Summarise the first section for me',
    tutorSuggest3: 'How am I doing, and what is due next?',
    tutorSuggest4: 'Explain the hardest topic in simple words',
    tutorHonestyTitle: 'Learning with integrity',
    tutorHonestyText: 'I will not give answers to graded questions, but I will help you understand them.',
    reviewsTitle: 'Learner reviews',
    reviewYours: 'Your review',
    reviewStarsLabel: '{stars} out of 5 stars',
    reviewEdited: 'edited',
    reviewEdit: 'Edit your review',
    reviewStar1: 'Poor',
    reviewStar2: 'Fair',
    reviewStar3: 'Good',
    reviewStar4: 'Very good',
    reviewStar5: 'Excellent',
    reviewDeleteConfirm: 'Delete your review of this course?',
    reviewEditTitle: 'Edit your review',
    reviewWriteTitle: 'Rate this course',
    reviewYourRating: 'Your rating',
    reviewNeedRating: 'Please choose a star rating.',
    reviewCommentLabel: 'Your review (optional)',
    reviewCommentPlaceholder: 'What did you like? What could be better? Your review helps other learners decide.',
    reviewPublicNote: 'Your first name and initial are shown with your review.',
    reviewError: 'Your review could not be saved. Please try again.',
    reviewUpdate: 'Update review',
    reviewSubmit: 'Post review',
    reviewDelete: 'Delete',
    reviewCancel: 'Cancel',
    reviewSignInPrompt: 'Have you taken this course?',
    reviewSignIn: 'Sign in to review it',
    reviewEnrolPrompt: 'Enrol in this course to rate and review it.',
    reviewAverageLabel: 'Rated {stars} out of 5',
    reviewNone: 'No reviews yet',
    reviewCount: '{count} reviews',
    reviewCountOne: '{count} review',
    reviewBeFirst: 'No reviews yet. Learners who take this course can be the first to review it.',
    reviewBeFirstShort: 'No reviews yet',
    reviewShowMore: 'Show more reviews',
    builderPageTitle: 'AI course builder',
    builderAddToCourse: 'Add with AI',
    builderIntro: 'Describe a course and the AI drafts an outline for you to review. Then it writes the lessons and quizzes straight into Studio.',
    builderHowTitle: 'How it works',
    builderHow1: 'Describe the course: topic, learners, level and language.',
    builderHow2: 'Review the outline. Rename, reorder, add or remove anything, or ask for a new draft.',
    builderHow3: 'Build it. The AI writes each unit\'s lesson text and quiz questions.',
    builderHow4: 'Check the content in Studio, edit what you need and publish when ready.',
    builderRecent: 'Your recent drafts',
    builderStatus_outlining: 'Drafting outline',
    builderStatus_outline_ready: 'Outline ready',
    builderStatus_building: 'Building',
    builderStatus_done: 'Built',
    builderStatus_failed: 'Failed',
    builderStep1: '1. Describe your course',
    builderPrompt: 'What should the course teach?',
    builderPromptPlaceholder: 'For example: An introduction to personal finance for college students in India: budgeting, saving, UPI safety, taxes and investing basics. Practical, with everyday examples.',
    builderTitle: 'Course title',
    builderAudience: 'Who is it for?',
    builderAudiencePlaceholder: 'For example: first-year college students',
    builderLevel: 'Level',
    builderLevel_beginner: 'Beginner',
    builderLevel_intermediate: 'Intermediate',
    builderLevel_advanced: 'Advanced',
    builderLanguage: 'Course language',
    builderSections: 'Sections',
    builderLessons: 'Lessons per section',
    builderQuizzes: 'A graded quiz at the end of each section',
    builderExam: 'A final exam',
    builderTarget: 'Where should it go?',
    builderTargetNew: 'Create a new course',
    builderKey_org: 'Organisation',
    builderKey_number: 'Course number',
    builderKey_run: 'Course run',
    builderTargetExisting: 'Add to a course I already have',
    builderChooseCourse: 'Choose a course…',
    builderExistingNote: 'New sections are added after the existing ones. Nothing already in the course is changed.',
    builderError: 'Something went wrong. Please try again.',
    builderWorking: 'Starting…',
    builderDraft: 'Draft the outline',
    builderDraftNote: 'Nothing is added to Studio until you approve the outline.',
    builderOutlining: 'Drafting your outline…',
    builderOutliningNote: 'This usually takes under a minute. You can leave this page and come back later.',
    builderStep2: '2. Review the outline',
    builderOutlineIntro: 'Edit anything you like. Each unit becomes a lesson with text and a practice question; quiz and exam subsections get graded questions.',
    builderShortDescription: 'Short description',
    builderCategory: 'Catalog category',
    builderCatalogLevel: 'Catalog level',
    builderTopics: 'Topics (up to {max})',
    builderSection: 'Section',
    builderSectionTitle: 'Section title',
    builderKind: 'Subsection type',
    builderKind_lesson: 'Lesson',
    builderKind_quiz: 'Quiz',
    builderKind_exam: 'Exam',
    builderSubsectionTitle: 'Subsection title',
    builderUnitTitle: 'Unit title',
    builderUnitGoal: 'What the learner should take away',
    builderNewUnit: 'New unit',
    builderAddUnit: 'Add unit',
    builderNewSubsection: 'New subsection',
    builderAddSubsection: 'Add subsection',
    builderNewSection: 'New section',
    builderAddSection: 'Add section',
    builderFeedback: 'Or ask for a new draft',
    builderFeedbackPlaceholder: 'For example: fewer sections, more hands-on examples, add a section on taxes.',
    builderRedraft: 'Redraft',
    builderSaved: 'Saved',
    builderSave: 'Save outline',
    builderBuild: 'Build {units} units',
    builderBuildConfirm: 'Build the course now? The AI will write {units} units in Studio. This can take a while.',
    builderMoveUp: 'Move up',
    builderMoveDown: 'Move down',
    builderRemove: 'Remove',
    builderStep3: '3. Building the course',
    builderCreating: 'Creating sections and units in Studio…',
    builderProgress: '{done} of {total} units written',
    builderProgressNote: 'You can leave this page; the build carries on.',
    builderDone: 'Your course is ready',
    builderDoneNote: 'The content is in Studio but not published. Review it, edit what you need and publish when ready.',
    builderFailed: 'The build stopped',
    builderOpenCourse: 'Open in Studio',
    builderAnother: 'Start a new course',
    builderRetry: 'Try again',
    facetCategory: 'Exam',
    facetLevel: 'Medium',
    facetTopic: 'Subject',
    signIn: 'Sign in',
    pathTitle: 'My Path',
    pathIntro: 'Your personal plan for this course: what to study next, what you can skip, and what to revise, based on your answers so far.',
    pathError: 'Something went wrong. Please try again.',
    pathPreparing: 'Your path for this course is being prepared',
    pathNotReady: 'Personalised paths are not available for this course yet',
    pathNotReadyNote: 'Once the course has been analysed, this page will show what to study next, what you can skip and what to revise.',
    pathStartTitle: 'Find your starting point',
    pathStartText: 'Take a short {count}-question check. We will suggest units you can skip and point out gaps to work on first.',
    pathNextUp: 'Next up',
    pathReviseFirst: 'Revise first',
    pathNextText: 'This unit covers: {concepts}.',
    pathReviseFirstText: 'You are finding {concepts} hard. Revising it first will make "{next}" much easier.',
    pathStart: 'Start learning',
    pathContinue: 'Go to unit',
    pathAllDone: 'You have covered everything in this course',
    pathAllDoneText: 'Keep your knowledge fresh with the reviews below, or look at what to learn next.',
    pathRevise: 'Revise',
    pathReviseNote: 'Concepts you are finding hard, and ones due for a quick review so you remember them.',
    pathRevisePrereq: 'Builds on: {concepts}',
    pathAskTutor: 'Ask the AI Tutor',
    pathConcepts: 'Your concepts',
    pathConceptsSummary: 'You have mastered {mastered} of {total} concepts in this course.',
    pathShowAll: 'Show all {count}',
    pathShowLess: 'Show fewer',
    pathState_mastered: 'Mastered',
    pathState_known: 'Probably known',
    pathState_learning: 'Learning',
    pathState_struggling: 'Needs revision',
    pathState_not_started: 'Not started',
    pathState_review_due: 'Review due',
    pathDiagTaken: 'Last result: {score} of {total}.',
    pathDiagNotTaken: 'A {count}-question check of what you already know.',
    pathPace: 'Your pace',
    pathUnitsDone: 'units completed',
    pathSkippable: 'You can probably skip {count} units you already know.',
    pathWeeksLeft: 'At your recent pace (about {perWeek} units a week), you will finish in about {weeks} weeks.',
    pathBehind: 'At this pace you may not finish before the course ends. Try to fit in a little more each week.',
    pathSkip: 'You can skip',
    pathSkipNote: 'You already seem to know what these units teach. Skim them or move on.',
    pathNextCourses: 'What to learn next',
    diagTitle: 'Knowledge check',
    diagIntro: 'Answer what you can: {count} questions, one per key idea of the course. It is not graded. If you do not know an answer, leave it blank.',
    diagSubmit: 'Check my answers ({answered} of {total})',
    diagChecking: 'Checking…',
    diagLater: 'Maybe later',
    diagSkipNote: 'Your answers only personalise your path; they do not count towards your grade.',
    diagScore: 'You got {score} of {total} right',
    diagScoreNote: 'Your path now suggests units you can skip and gaps to work on first.',
    diagRight: 'Correct.',
    diagWrong: 'The answer is: {answer}',
    diagSeePath: 'See my path',
    diagStart: 'Start the check',
    diagRetake: 'Take it again',
    nudgeRevise: 'This unit builds on {concepts}, which you are finding hard. Revise first:',
    nudgeStruggling: 'You are finding {concepts} hard. Take your time here, and ask the AI Tutor if you are stuck.',
    nudgeSkip: 'You already seem to know what this unit covers. Skim it or move on.',
    nudgeOpenPath: 'My Path',
    nudgeDismiss: 'Dismiss',
    lpTitle: 'My learning path',
    lpSummary: 'Tell us your goal and the time you have. AI plans which courses to take, in what order.',
    lpSignIn: 'Sign in to plan a learning path based on your goal, your level and the courses you have already taken.',
    lpPlan: 'Plan my learning path',
    lpYourGoal: 'Your goal',
    lpGoalLabel: 'What do you want to achieve?',
    lpGoalPlaceholder: 'For example: Get a job as a data analyst within a year, or start selling my handicrafts online.',
    lpGoalShort: 'Describe your goal in a sentence or two.',
    lpTopics: 'Topics you are interested in (optional)',
    lpLevel: 'Your level',
    lpLevelUnsure: 'Not sure',
    lpHours: 'Hours a week',
    lpTarget: 'Target date (optional)',
    lpCreate: 'Plan my path',
    lpUpdate: 'Update my path',
    lpCancel: 'Cancel',
    lpEdit: 'Change goal',
    lpHoursShort: '{hours} hours a week',
    lpBy: 'by {date}',
    lpPrivacy: 'Your goal and course history are used only to plan your path.',
    lpLimit: 'You have planned several paths today. Please try again tomorrow.',
    lpWorking: 'Planning your path…',
    lpWorkingNote: 'This takes a few seconds.',
    lpFailed: 'We could not plan your path right now. Please try again later.',
    lpYourPath: 'Your path',
    lpPassed: 'Completed',
    lpProgress: 'Enrolled · {percent}% so far',
    // Learning path page redesign (thervumitra/adaptive/LearningPathPage.jsx).
    lpPlanTitle: 'Plan your learning path',
    lpChangeTitle: 'Change your goal',
    lpPlanText: 'Answer four quick questions. AI then picks courses from our catalog and puts them in order for you.',
    lpExamplesLabel: 'Try:',
    lpExample1: 'Get a job as a data analyst within a year.',
    lpExample2: 'Start selling my handicrafts online.',
    lpExample3: 'Prepare for government job exams.',
    lpTopicsPicked: '{count} of {max} picked',
    lpShowAllTopics: 'Show all {count} topics',
    lpShowFewerTopics: 'Show fewer topics',
    lpLevelUnsureText: 'AI decides from your courses so far',
    lpLevelBeginnerText: 'New to the subject',
    lpLevelIntermediateText: 'I know the basics',
    lpLevelAdvancedText: 'I want to go deeper',
    lpTime: 'Your time',
    lpHowTitle: 'How it works',
    lpHow1Title: 'Tell us your goal',
    lpHow1Text: 'A job, a skill or an exam, in your own words and your own language.',
    lpHow2Title: 'AI plans your courses',
    lpHow2Text: 'It picks courses from our catalog and orders them for your level and the time you have.',
    lpHow3Title: 'Learn step by step',
    lpHow3Text: 'Your path shows what is done and what comes next, and keeps up as you learn.',
    lpProgressTitle: 'Your progress',
    lpDoneCount: '{done} of {total} courses completed',
    lpNextUp: 'Up next',
    lpInProgress: 'In progress',
    lpNotStarted: 'Not started',
    lpStepLabel: 'Step {n}',
    lpWhy: 'Why this course:',
    lpFocus: 'Focus on:',
    lpStart: 'Start course',
    lpContinue: 'Continue',
    lpReview: 'Review',
    lpFinishTitle: 'Goal reached',
    lpNotNow: 'Left out for now',
    lpUpdated: 'Planned on {date}',
    lpTryAgain: 'Try again',
    cmTitle: 'Concept map',
    cmButton: 'Concept map',
    cmIntro: 'Adaptive learning uses this map to track what each learner has mastered, recommend units, suggest what to skip and trigger revision.',
    cmBackToCourse: 'Back to the course outline',
    cmNoCourse: 'Open this page from a course outline.',
    cmDenied: 'You need to be on the course team to edit its concept map.',
    cmDrafting: 'The AI is drafting the concept map…',
    cmDraftingNote: 'This usually takes under a minute. You can leave this page.',
    cmEmptyTitle: 'No concept map yet',
    cmEmptyText: 'The AI drafts one automatically a few minutes after the course is published, or you can draft it now.',
    cmDraft: 'Draft with AI',
    cmRedraft: 'Redraft with AI',
    cmRedraftConfirm: 'Replace this map with a new AI draft? Your edits to it will be lost.',
    cmAiDraft: 'AI draft, not reviewed yet',
    cmReviewedBy: 'Reviewed by {user}',
    cmStale: 'The course has changed since this map was reviewed.',
    cmRefresh: 'Update the unit list',
    cmTab_concepts: 'Concepts',
    cmTab_units: 'Units',
    cmTab_diagnostic: 'Diagnostic',
    cmConceptName: 'Concept name',
    cmDescription: 'What knowing this concept means',
    cmPrereqs: 'Builds on:',
    cmAddPrereq: 'Add prerequisite',
    cmNewConcept: 'New concept',
    cmAddConcept: 'Add concept',
    cmAddConceptToUnit: 'Add concept',
    cmGraded: 'graded',
    cmConcept: 'Concept',
    cmQuestion: 'Question',
    cmChoice: 'Choice {n}',
    cmCorrect: 'Correct answer',
    cmExplanation: 'Explanation shown after the check',
    cmAddQuestion: 'Add question',
    cmSave: 'Save draft',
    cmApprove: 'Save and approve',
    cmApproved: 'Approved',
    gmTitle: 'My progress',
    gmAvatar: 'Your avatar',
    gmLevel: 'Level',
    gmLevelN: 'Level {level}',
    gmLevelProgress: 'Progress to the next level',
    gmToNext: '{xp} XP to level {level}',
    gmLevelUp: 'Level up! You reached level {level}.',
    gmStreak: 'Streak',
    gmDaysN: '{n} days',
    gmLongest: 'Longest {n}',
    gmTokens: '{n} streak freezes',
    gmBadges: 'Badges',
    gmAchievements: 'achievements',
    gmToday: 'Today',
    gmRank: 'Rank',
    gmShow: 'Show',
    gmHide: 'Hide',
    gmRemindStreak: 'Keep your {n}-day streak alive: do one lesson before midnight.',
    gmRemindGoal: '{xp} XP to go for today\'s goal.',
    gmTab_overview: 'Overview',
    gmTab_quests: 'Quests',
    gmTab_achievements: 'Achievements',
    gmTab_leaderboard: 'Leaderboard',
    gmTab_avatar: 'Avatar',
    gmTab_settings: 'Settings',
    gmGoals: 'Goals',
    gmDailyGoal: 'Daily goal',
    gmDailyDone: 'Daily goal done!',
    gmDailyDoneText: 'Great work. Anything more today is a bonus.',
    gmDailyLeft: '{xp} XP to reach today\'s goal.',
    gmWeeklyTarget: '{done} of {target} learning days this week',
    gmDays: 'M,T,W,T,F,S,S',
    gmActiveQuests: 'Active quests',
    gmSeeAll: 'See all',
    gmNoQuests: 'No active quests right now.',
    gmRecommended: 'Recommended next',
    gmNoRecs: 'You are all caught up. Explore a new course!',
    gmRecGoal: 'Earn {xp} more XP to meet today\'s goal.',
    gmRecRevise: 'Revise {concept} in {course}.',
    gmRecNext: 'Next in {course}: {unit}.',
    gmRecQuest: '{quest}: {left} to go for +{xp} XP.',
    gmRecPath: 'Your learning path suggests a new course.',
    gmGo: 'Go',
    gmView: 'View',
    gmCourses: 'Courses in progress',
    gmNoCourses: 'Enrol in a course to start earning XP.',
    gmPercent: '{percent}% complete',
    gmThisWeek: 'This week',
    gmWeekUp: '{xp} XP more than last week.',
    gmWeekDown: '{xp} XP less than last week.',
    gmWeekFirst: 'Your first week of tracking.',
    gmRecentAchievements: 'Recent achievements',
    gmNoBadges: 'Complete your first lesson to earn a badge.',
    gmXpHistory: 'XP history',
    gmNoXp: 'XP you earn will show up here.',
    gmStats: 'Your statistics',
    gmBestDay: 'Best day',
    gmBestWeek: 'Best week',
    gmMastery: 'Concept mastery',
    gmMasteryOf: '{mastered} of {total} mastered',
    gmStat_lessons: 'Lessons',
    gmStat_videos: 'Videos',
    gmStat_quizzes: 'Quizzes passed',
    gmStat_perfect: 'Perfect scores',
    gmStat_problems: 'Problems solved',
    gmStat_courses: 'Courses completed',
    gmStat_concepts: 'Concepts mastered',
    gmStat_forum_posts: 'Discussion posts',
    gmStat_active_days: 'Active days',
    gmRule_lesson: 'Lesson completed',
    gmRule_video: 'Video completed',
    gmRule_quiz_pass: 'Quiz passed',
    gmRule_quiz_perfect: 'Perfect score bonus',
    gmRule_assignment: 'Assignment completed',
    gmRule_course: 'Course completed',
    gmRule_daily_goal: 'Daily goal met',
    gmRule_weekly_goal: 'Weekly target met',
    gmRule_problem: 'Problem solved',
    gmRule_forum_post: 'Discussion post',
    gmRule_concept: 'Concept mastered',
    gmRule_streak: 'Streak milestone',
    gmRule_badge: 'Badge earned',
    gmRule_quest: 'Quest completed',
    gmKind_daily: 'Daily',
    gmKind_weekly: 'Weekly',
    gmKind_course: 'Course',
    gmKind_subject: 'Subject',
    gmKind_mission: 'Mission',
    gmKind_assessment: 'Challenge',
    gmKinds_daily: 'Daily quests',
    gmKinds_weekly: 'Weekly quests',
    gmKinds_course: 'Course quests',
    gmKinds_subject: 'Subject challenges',
    gmKinds_mission: 'Missions',
    gmKinds_assessment: 'Assessment challenges',
    gmKindsNote_daily: 'New every day.',
    gmKindsNote_weekly: 'New every Monday.',
    gmKindsNote_course: 'For a specific course.',
    gmKindsNote_subject: 'Across all courses in one subject.',
    gmKindsNote_mission: 'Complete the steps in order to unlock the next one.',
    gmKindsNote_assessment: 'Test yourself with quizzes and exams.',
    gmBadgesOf: '{earned} of {total} badges earned',
    gmEarnedOn: 'Earned {date}',
    gmBoard_weekly: 'This week',
    gmBoard_global: 'All time',
    gmBoard_course: 'Course',
    gmBoardEmpty: 'No one on this leaderboard yet.',
    gmYouHidden: 'You (only you see this)',
    gmYourRank: 'You are #{rank} with {xp} XP.',
    gmPersonalBests: 'Personal bests',
    gmLongestStreak: 'Longest streak',
    gmOptInText: 'Show up on leaderboards under a nickname of your choice. Your real name is never shown.',
    gmOptIn: 'Join leaderboards',
    gmSaveAvatar: 'Save avatar',
    gmNone: 'None',
    gmUnlockLevel: 'Level {level}',
    gmUnlockBadge: 'Earn a badge',
    gmLook_skin: 'Skin',
    gmLook_hair: 'Hair',
    gmLook_hair_color: 'Hair colour',
    gmLook_outfit: 'Outfit',
    gmLook_background: 'Background',
    gmHair_short: 'Short',
    gmHair_long: 'Long',
    gmHair_bun: 'Bun',
    gmHair_curly: 'Curly',
    gmHair_ponytail: 'Ponytail',
    gmHair_none: 'None',
    gmSlot_eyes: 'Eyewear',
    gmSlot_head: 'Headwear',
    gmSlot_badge: 'Pin',
    gmSlot_frame: 'Frame',
    gmDailyGoalSetting: 'Daily goal',
    gmWeeklyTargetSetting: 'Learning days a week',
    gmGoal_30: 'Casual · {xp} XP',
    gmGoal_60: 'Regular · {xp} XP',
    gmGoal_100: 'Serious · {xp} XP',
    gmGoal_150: 'Intense · {xp} XP',
    gmAutoFreeze: 'Use a streak freeze automatically if I miss a day (you have {tokens}).',
    gmReminders: 'Reminders',
    gmRemindersOn: 'Remind me on this site when my streak or daily goal is at risk',
    gmReminderTime: 'From this time',
    gmReminderDays: 'On these days',
    gmTimeZone: 'Days are counted in your time zone: {tz}.',
    gmLeaderboard: 'Leaderboards',
    gmShowMe: 'Show me on leaderboards',
    gmNickname: 'Nickname',
    gmNicknamePlaceholder: 'For example: CodeTiger',
    gmNicknameRequired: 'Choose a nickname to join leaderboards.',
    gmNicknameInvalid: 'Use 3 to 30 letters, numbers, spaces, dots or dashes.',
    gmPrivacy: 'Only your nickname, level, avatar and XP are shown to others.',
    gmSaveSettings: 'Save settings',
    facetPricing: 'Price',
    shFree: 'Free',
    shPaid: 'Paid',
    shOff: '{percent}% off',
    shOwned: 'Purchased',
    shPrice: 'Price',
    shBuyNow: 'Buy now',
    shAddToCart: 'Add to cart',
    shGoToCart: 'Go to cart',
    shWishAdd: 'Add to wishlist',
    shWishRemove: 'Remove from wishlist',
    shGstIncluded: 'Price includes 18% GST. Lifetime access to the course.',
    shOfferEnds: '{title} ends {date}',
    shEnrollFree: 'Enroll for free',
    shEnrolling: 'Enrolling…',
    shStartCourse: 'Start the course',
    shCart: 'Cart',
    shCartCount: 'Cart, {n} items',
    shWishlist: 'Wishlist',
    shMyOrders: 'My orders',
    shShop: 'Shopping',
    shSignInToShop: 'Sign in to see your cart, wishlist and orders.',
    shSignIn: 'Sign in',
    shCourse: 'Course',
    shSubtotal: 'Subtotal',
    shCouponLine: 'Coupon {code}',
    shTotal: 'Total',
    shTaxable: 'Taxable value',
    shIgst: 'IGST ({rate}%)',
    shCgst: 'CGST ({rate}%)',
    shSgst: 'SGST ({rate}%)',
    shGstOf: 'Includes GST of {amount}',
    shCouponApplied: 'Coupon {code} applied',
    shRemove: 'Remove',
    shHaveCoupon: 'Have a coupon?',
    shCouponPlaceholder: 'Coupon code',
    shApply: 'Apply',
    shCartEmpty: 'Your cart is empty.',
    shBrowse: 'Browse courses',
    shSaveForLater: 'Save for later',
    shSkippedOwned: 'You already own this, so it will not be charged.',
    shSkippedFree: 'This is free now: enroll from its page.',
    shBundleHint: 'Buying several courses together? Bundle offers are applied automatically.',
    shSummary: 'Order summary',
    shCheckout: 'Checkout',
    shSecure: 'Secure payment by Razorpay: UPI, cards, net banking and wallets.',
    shStateRequired: 'Choose your state (place of supply) for the GST invoice.',
    shPaymentCancelled: 'Payment was cancelled. You have not been charged.',
    shPaymentFailed: 'Payment failed. {reason}',
    shBilling: 'Billing details',
    shBillName: 'Name on invoice',
    shBillState: 'State',
    shChooseState: 'Choose your state',
    shBillGstin: 'GSTIN',
    shOptional: '(optional)',
    shGstinHelp: 'For a business invoice with input tax credit.',
    shBillAddress: 'Billing address',
    shInvoiceTo: 'The tax invoice will be available under My orders ({email}).',
    shPay: 'Pay {amount}',
    shPlaceFree: 'Place order',
    shSecureRazorpay: 'You will pay in the secure Razorpay window.',
    shTerms: 'By paying you agree to the',
    shTermsLink: 'terms of service',
    shNoOrders: 'No orders yet.',
    shOrderTitle: 'Order {number}',
    shThanks: 'Thank you! Your payment was successful.',
    shAccessReady: 'You are enrolled and can start learning right away.',
    shGoDashboard: 'Go to my courses',
    shConfirming: 'Confirming your payment…',
    shBackToCart: 'Back to cart',
    shPrintInvoice: 'Print or save invoice',
    shTaxInvoice: 'Tax invoice',
    shOrderSummary: 'Order summary',
    shInvoiceNo: 'Invoice no. {number}',
    shOrderNo: 'Order {number}',
    shPaymentRef: 'Payment ref. {id}',
    shBilledTo: 'Billed to',
    shPlaceOfSupply: 'Place of supply: {state} ({code})',
    shItem: 'Item',
    shDiscount: 'Discount',
    shAmount: 'Amount',
    shTotalPaid: 'Total (incl. GST)',
    shCouponUsed: 'Coupon used: {code}',
    shInvoiceFoot: 'Online education services. This is a computer-generated invoice and needs no signature.',
    shMoveToCart: 'Move to cart',
    shWishEmpty: 'Your wishlist is empty. Tap the heart on a course to save it here.',
    shErrorGeneric: 'Something went wrong. Please try again.',
    shStatus_paid: 'Paid',
    shStatus_created: 'Payment pending',
    shStatus_failed: 'Payment failed',
    shStatus_cancelled: 'Cancelled',
    shStatus_refunded: 'Refunded',
    shStatusNote_failed: 'This payment failed and you were not charged.',
    shStatusNote_cancelled: 'This order was cancelled and you were not charged.',
    shStatusNote_refunded: 'This order was refunded. Course access has been removed.',
    shCoupon_invalid: 'That coupon code is not valid.',
    'shCoupon_used-up': 'This coupon has been fully used.',
    'shCoupon_already-used': 'You have already used this coupon.',
    'shCoupon_not-applicable': 'This coupon does not apply to the items in your cart.',
    'shCoupon_min-order': 'Your order is below this coupon\'s minimum.',
    shError_owned: 'You already own this.',
    'shError_not-for-sale': 'This is not for sale.',
    'shError_cart-full': 'Your cart is full. Check out or remove something first.',
    'shError_payments-not-configured': 'Online payments are not available yet. Please try again later.',
    'shError_gateway-unreachable': 'The payment service could not be reached. Please try again.',
    'shError_gateway-error': 'The payment service returned an error. Please try again.',
    shError_gateway_script: 'The payment window could not be loaded. Check your connection and try again.',
    'shError_coupon-invalid': 'That coupon code is not valid.',
    'shError_coupon-used-up': 'This coupon has been fully used.',
    'shError_coupon-already-used': 'You have already used this coupon.',
    'shError_coupon-not-applicable': 'This coupon does not apply to the items in your cart.',
    'shError_coupon-min-order': 'Your order is below this coupon\'s minimum.',
    'shError_nothing-to-buy': 'There is nothing to pay for in your cart.',
    shError_gstin: 'That GSTIN does not look right.',
    'shError_payment-required': 'This course is paid: buy it to enroll.',
    'shError_not-paid': 'Only paid orders can be refunded.',
    shError_item: 'Something went wrong. Please try again.',
    shAdmTitle: 'Commerce',
    shAdmIntro: 'Prices, offers, coupons and orders for courses and plans. Prices include GST.',
    shAdmDenied: 'Only platform staff can manage commerce.',
    shAdmTab_overview: 'Overview',
    shAdmTab_prices: 'Prices',
    shAdmTab_plans: 'Plans',
    // Studio admin pages (thervumitra/admin/AdminShell.jsx: Live classes, Commerce, Mock papers).
    admLearners: 'Learners enrolled',
    admSearchCourses: 'Search your courses',
    admShowingN: '{n} shown',
    admNoMatch: 'Nothing matches your search.',
    admMoreActions: 'More actions',
    admSettings: 'Settings',
    admSummary: 'Summary',
    admPhases: 'Phases',
    admCoursesLabel: 'Courses',
    admEnrolled: 'Enrolled',
    admAllCourses: 'All courses',
    admRecordings: 'Recordings',
    admNextClass: 'Next class',
    admCatalog: 'Paid catalog',
    admPaidCourses: 'Paid courses',
    admRefunds: 'Refunds',
    admDesc_prices: 'Set course prices and MRP.',
    admDesc_plans: 'Vidya passes: length, price and perks.',
    admDesc_coupons: 'Codes learners enter at checkout.',
    admDesc_offers: 'Automatic sales and bundle discounts.',
    admDesc_orders: 'Payments, invoices and refunds.',
    admPagination: 'Pages',
    admRange: '{from}–{to} of {total}',
    admPerPage: 'Rows per page',
    admPrev: 'Previous page',
    admNext: 'Next page',
    admPageN: 'Page {n}',
    admPricing: 'Price',
    admAnyPrice: 'Any price',
    admCourse: 'Course',
    admActive: 'Active',
    admInactive: 'Off',
    admSearch_coupons: 'Search coupons by code or description',
    admSearch_offers: 'Search offers by title or badge',
    admSearch_plans: 'Search plans by name or id',
    admOrdersHint: 'The latest 200 orders with this status. Press Enter to search by order number, invoice or learner.',
    shAdmPlansNote: 'Plans are the passes sold on a service’s page (Vidya: thervumitra.com/vidya). Learners see the plans on sale, lowest order first. Buying a plan again extends access.',
    shAdmPlanNew: 'New plan',
    shAdmPlanCreate: 'Create plan',
    shAdmPlanRef: 'Plan id',
    shAdmPlanTitle: 'Name',
    shAdmPlanService: 'Service',
    shAdmPlanDays: 'Length (days)',
    shAdmPlanDaysN: '{n} days',
    shAdmPlanBadge: 'Badge',
    shAdmPlanBadgeHint: 'e.g. Best value',
    shAdmPlanSort: 'Order',
    shAdmPlanDescription: 'Short description',
    shAdmPlanPerks: 'What is included (one per line)',
    shAdmPlanPerksHint: 'Every Vidya mode\nNotebooks and mistake review',
    shAdmPlanDelete: 'Delete plan',
    shAdmPlanDeleteConfirm: 'Delete this plan? A plan that was ever bought is taken off sale instead, to keep the order history.',
    shAdmPlanOff: 'Off sale',
    shAdmPlanSold: '{n} sold',
    shAdmErr_ref: 'Use 2 to 63 lowercase letters, digits and hyphens for the plan id.',
    'shAdmErr_ref-taken': 'A plan with this id already exists.',
    shAdmTab_coupons: 'Coupons',
    shAdmTab_offers: 'Offers',
    shAdmTab_orders: 'Orders',
    shAdm_today: 'Today',
    shAdm_week: 'Last 7 days',
    shAdm_month: 'Last 30 days',
    shAdm_all: 'All time',
    shAdmOrdersN: '{n} orders',
    shAdmNoGateway: 'Razorpay keys are not configured, so learners cannot pay yet (free and 100% coupon orders still work).',
    shAdmCatalogFacts: '{courses} paid courses · {refunded} refunds',
    shAdmSearch: 'Search by title or ID',
    shAdmKind: 'Type',
    shAdmAll: 'All',
    shAdmCourses: 'Courses',
    shAdmSearchBtn: 'Search',
    shAdmPricesNote: 'Price 0 makes an item free. MRP is optional (shown struck through). Paid courses can only be joined by buying them.',
    shAdmPrice: 'Price (₹)',
    shAdmMrp: 'MRP (₹)',
    shAdmOnSale: 'On sale',
    shAdmLive: 'Learners see',
    shAdmSave: 'Save',
    shAdmSaved: 'Saved',
    shAdmCancel: 'Cancel',
    shAdmEditBtn: 'Edit',
    shAdmErr_invalid: 'Enter a valid price.',
    'shAdmErr_list-below-price': 'MRP cannot be below the price.',
    shAdmIntro_coupons: 'Codes learners enter at checkout.',
    shAdmIntro_offers: 'Sales lower prices automatically; bundles discount carts with enough items.',
    shAdmNew_coupons: 'New coupon',
    shAdmNew_offers: 'New offer',
    shAdmEdit_coupons: 'Edit coupon',
    shAdmEdit_offers: 'Edit offer',
    shAdmNone: 'Nothing here yet.',
    shAdmAlways: 'Always',
    shAdmUsed: 'Used',
    shAdmWhen: 'Valid',
    shAdmMinOrder: 'min. {amount}',
    shAdmMinItems: '{n}+ items',
    shAdmDeleteConfirm: 'Delete this? Used coupons are switched off instead, to keep the order history.',
    shAdmPercent: 'Percent (%)',
    shAdmFixed: 'Fixed amount (₹)',
    shAdmKind_sale: 'Sale',
    shAdmKind_bundle: 'Bundle',
    shAdmF_code: 'Code',
    shAdmF_description: 'Description',
    shAdmF_discount_type: 'Discount type',
    shAdmF_value: 'Discount',
    shAdmF_max_discount: 'Maximum discount (₹)',
    shAdmF_min_order: 'Minimum order (₹)',
    shAdmF_usage_limit: 'Total uses',
    shAdmF_per_user_limit: 'Uses per learner',
    shAdmF_categories: 'Categories',
    shAdmF_products: 'Courses',
    shAdmF_starts: 'Starts',
    shAdmF_ends: 'Ends',
    shAdmF_active: 'Active',
    shAdmF_title: 'Title',
    shAdmF_badge: 'Badge',
    shAdmF_kind: 'Type',
    shAdmF_min_items: 'Minimum items',
    shAdmH_code: '3 to 32 letters, digits, - or _.',
    shAdmH_max_discount: '0 means no cap.',
    shAdmH_min_order: '0 means any order.',
    shAdmH_usage_limit: '0 means unlimited.',
    shAdmH_per_user_limit: '0 means unlimited.',
    shAdmH_categories: 'Comma-separated catalog categories, e.g. Finance & Money. Empty means all.',
    shAdmH_products: 'Comma-separated course IDs. Empty means all.',
    shAdmH_badge: 'Short label on course cards, e.g. Diwali sale.',
    shAdmH_min_items: 'Items in the cart needed for the bundle discount.',
    shAdmE_number: 'Enter a number.',
    shAdmE_date: 'Enter a valid date.',
    shAdmE_choice: 'Choose an option.',
    shAdmE_code: '3 to 32 letters, digits, - or _.',
    shAdmE_taken: 'That code is already used.',
    shAdmE_percent: 'A percentage cannot be over 100.',
    shAdmE_required: 'Required.',
    'shAdmE_before-start': 'The end must be after the start.',
    shAdmOrderSearch: 'Order number, username or email',
    shAdmOrder: 'Order',
    shAdmLearner: 'Learner',
    shAdmStatus: 'Status',
    shAdmRefund: 'Refund',
    shAdmRefundPrompt: 'Refund {number} ({amount}) in full and remove course access? Reason:',
    shAdmRefunded: '{number} refunded.',
    anLink: 'Analytics',
    anTitle: 'Learning analytics',
    anIntro: 'Charts and reports on how learners enrol, study and perform, with answers to your questions.',
    anPlatform: 'Whole platform',
    anScope: 'Show analytics for',
    anRefresh: 'Refresh',
    anUpdated: 'Updated at {time}',
    anLoading: 'Preparing your dashboard…',
    anLoadError: 'The analytics could not be loaded. Please try again.',
    anNoAccess: 'Analytics are available to course instructors, course staff and platform staff. You do not teach any course yet.',
    anNoAccessCourse: 'You do not have access to this course\'s analytics.',
    anPickCourse: 'Choose a course',
    anOverview: 'Overview',
    anPinnedTitle: 'Your pinned reports',
    anCourseTable: 'Course performance',
    anCourse: 'Course',
    anKpi_enrolled: 'Enrolled learners',
    anKpi_new_week: 'New this week',
    anKpi_active_week: 'Active this week',
    anKpi_avg_progress: 'Average progress',
    anKpi_avg_grade: 'Average grade',
    anKpi_pass_rate: 'Pass rate',
    anKpi_certificates: 'Certificates',
    anKpi_at_risk: 'Learners at risk',
    anKpi_rating: 'Rating',
    anKpi_users: 'Registered users',
    anKpi_new_users: 'New users (30 days)',
    anKpi_enrollments: 'Enrollments',
    anKpi_revenue_month: 'Sales (30 days)',
    anRep_enroll_trend: 'Enrollments per week',
    anRep_active_daily: 'Active learners per day',
    anRep_activity_mix: 'Learning activity per week',
    anRep_risk: 'Dropout risk',
    anRep_at_risk: 'Learners at risk',
    anRep_funnel: 'Learners reaching each section',
    anRep_grade_dist: 'Grade distribution',
    anRep_assess_scores: 'Average score by quiz or assignment',
    anRep_hardest: 'Hardest problems',
    anRep_mastery: 'Concept mastery',
    anRep_education: 'Learners by education',
    anRep_age: 'Learners by age',
    anRep_gender: 'Learners by gender',
    anRep_ratings: 'Ratings',
    anRep_registrations: 'New registrations per week',
    anRep_by_category: 'Enrollments by category',
    anRep_top_courses: 'Top courses by enrollment',
    anRep_pass_rates: 'Pass rate by course',
    anRep_revenue: 'Sales per month',
    anRep_revenue_items: 'Best-selling courses and passes',
    anRep_weekday: 'When learners study',
    anRep_country: 'Learners by country',
    anAskTitle: 'Ask AI about your learning data',
    anAskIntroCourse: 'Ask in your own words about this course: engagement, progress, grades, hard topics, learners at risk. The AI builds the charts for you.',
    anAskIntroPlatform: 'Ask in your own words about the whole platform: growth, courses, learners, completion and sales. The AI builds the charts for you.',
    anAskPlaceholder: 'e.g. Which quiz do learners struggle with most?',
    anAsk: 'Ask',
    anThinking: 'Analysing the data…',
    anClear: 'Clear conversation',
    anPrivacy: 'The AI sees only the names of the data fields and aggregated results, never learner names or emails.',
    anCannotAnswer: 'This question cannot be answered from the learning data available.',
    anAskError_limit: 'You have asked many questions in a short time. Please wait a while and try again.',
    anAskError_budget: 'The AI has reached its daily limit. Please try again tomorrow.',
    anAskError_unavailable: 'The AI could not answer right now. Please try again.',
    anAskError_incomplete: 'The answer was incomplete. Please rephrase the question and try again.',
    anAskError_question: 'Please write a longer question.',
    anExC1: 'How many learners were active each week in the last two months?',
    anExC2: 'Which problems have the lowest average score?',
    anExC3: 'Where do learners drop off in the course?',
    anExC4: 'List the learners at risk who have not been active for two weeks',
    anExP1: 'Which categories grew the most in the last 3 months?',
    anExP2: 'Compare pass rates across courses',
    anExP3: 'How many new learners registered each month this year?',
    anExP4: 'Which courses sold the most this month?',
    anReportError: 'This report could not be prepared.',
    anChartType: 'Chart type',
    anCsv: 'Download as CSV',
    anView_number: 'Number',
    anView_bar: 'Bars',
    anView_hbar: 'Ranked bars',
    anView_line: 'Line',
    anView_area: 'Area',
    anView_pie: 'Pie',
    anView_donut: 'Donut',
    anView_stacked: 'Stacked bars',
    anView_table: 'Table',
    anPin: 'Pin to dashboard',
    anPinned: 'Pinned',
    anUnpin: 'Remove',
    anNoData: 'No data for this yet.',
    anMore: '{n} more not shown.',
    anTruncated: 'Based on the most recent records only.',
    toolsMenu: 'ThervuMitra tools: AI course builder, analytics, live classes',
    lvTitle: 'Live classes',
    lvIntro: 'Schedule live classes on Zoom, run them, and follow attendance and recordings.',
    lvTimesIn: 'Times are shown in your time zone ({zone}).',
    lvUpcoming: 'Upcoming classes',
    lvPast: 'Past classes',
    lvNoUpcoming: 'No live classes are scheduled right now.',
    lvEmailReminders: 'Email me reminders',
    lvSubscribe: 'Copy calendar link',
    lvSubscribeHelp: 'Add this link to Google Calendar, Outlook or your phone to see every class automatically.',
    lvCopied: 'Link copied',
    lvNotConfigured: 'Live classes are being set up. Joining will be possible soon.',
    lvEnrolFirst: 'Enrol in this course to see its live classes.',
    lvLoadError: 'Live classes could not be loaded. Please try again.',
    lvMinutes: '{n} min',
    lvStartingNow: 'Starting now',
    lvInMinutes: 'in {n} min',
    lvInHours: 'in {n} h',
    lvInDays: 'in {n} days',
    lvPhase_upcoming: 'Upcoming',
    lvPhase_open: 'Open to join',
    lvPhase_live: 'Live now',
    lvPhase_ended: 'Ended',
    lvPhase_cancelled: 'Cancelled',
    lvJoin: 'Join class',
    lvStart: 'Start class',
    lvJoinHost: 'Join as host',
    'lvJoinError_not-started': 'Waiting for the instructor to open the class… this page will join you automatically.',
    'lvJoinError_too-early': 'The class is not open yet.',
    lvJoinError_ended: 'This class has ended.',
    lvJoinError_cancelled: 'This class was cancelled.',
    'lvJoinError_not-enrolled': 'Only learners enrolled in the course can join.',
    'lvJoinError_not-configured': 'Live classes are not available yet.',
    lvJoinError_unavailable: 'The class could not be opened. Please try again in a moment.',
    lvAddCalendar: 'Add to calendar',
    lvGoogleCalendar: 'Google Calendar',
    lvHost: 'Host: {name}',
    lvForLesson: 'For the lesson: {lesson}',
    lvOpensAt: 'You can join from {time}',
    lvYouAttended: 'You attended ({n} min)',
    lvYouMissed: 'You missed this class',
    lvRecordingSoon: 'The recording will appear here once the instructor publishes it.',
    lvWatch: 'Watch recording ({n} min)',
    lvCaptionsIn: 'Captions: {langs}',
    lvShowTranscript: 'Show transcript',
    lvHideTranscript: 'Hide transcript',
    lvLessonClass: 'Live class for this lesson: {title}',
    lvDashTitle: 'Upcoming live classes',
    lvDetails: 'Details',
    lvMore: '{n} more in your courses.',
    lvPickCourse: 'Choose a course to schedule its live classes.',
    lvNoCourses: 'You do not teach any course yet.',
    lvForbidden: 'Only the course team can manage its live classes.',
    lvServerMissing: 'Zoom is not connected yet: you can schedule classes now; the Zoom meetings are created and starting and joining work once the Zoom app keys are added.',
    lvTab_upcoming: 'Upcoming',
    lvTab_past: 'Past',
    lvTab_report: 'Report',
    lvScheduleClass: 'Schedule a class',
    lvEditClass: 'Edit class',
    lvNoneScheduled: 'No classes scheduled. Schedule one to get started.',
    lvNonePast: 'No classes held yet.',
    lvF_title: 'Title',
    lvF_description: 'Description (shown to learners)',
    lvF_date: 'Date',
    lvF_time: 'Start time',
    lvF_timezone: 'Time zone',
    lvF_duration: 'Duration',
    lvF_unit: 'Lesson',
    lvWholeCourse: 'Whole course (no specific lesson)',
    lvF_host: 'Host',
    lvMe: 'Me',
    lvF_co_hosts: 'Co-hosts',
    lvCoHostsHelp: 'Add course staff in Studio (Settings → Course team) to choose co-hosts.',
    lvRoomOptions: 'Room options',
    lvO_waiting_room: 'Waiting room: learners wait until a host admits them',
    lvO_record: 'Record the class (on the free Zoom plan, record to your computer and add the link afterwards)',
    lvO_mute_on_start: 'Mute learners when they join',
    lvO_breakout_rooms: 'Breakout rooms for group work',
    lvO_webcams_only_for_moderators: 'Turn off learners\' video when they join',
    lvF_welcome: 'Welcome message in the room',
    lvF_repeat: 'Repeat',
    lvOnce: 'Only once',
    lvWeeks: 'Every week, {n} classes',
    lvAnnounce: 'Email enrolled learners about the new class',
    lvRemindersNote: 'Learners get reminder emails a day and 15 minutes before the class, and can join from {early} minutes before the start. Hosts can open the room at any time.',
    lvSave: 'Save changes',
    lvSchedule: 'Schedule',
    lvCancelEdit: 'Cancel',
    lvErr_title: 'Enter a title (3 to 200 characters).',
    lvErr_date: 'Choose a valid date and time.',
    lvErr_timezone: 'Choose a time zone.',
    lvErr_duration: 'Choose a duration between 10 and 300 minutes.',
    lvErr_unit: 'Choose a lesson of this course.',
    lvErr_host: 'The host must be on the course team.',
    lvErr_number: 'Enter a number.',
    lvErr_generic: 'Something went wrong. Please try again.',
    'lvErr_not-configured': 'Zoom is not connected yet.',
    lvErr_unreachable: 'Zoom could not be reached.',
    lvErr_file: 'Choose a captions file (VTT or SRT, up to 5 MB).',
    lvErr_captions: 'That file is not a valid VTT or SRT captions file.',
    lvWeekly: 'weekly series',
    lvAttendedN: '{n} of {total} attended',
    lvEdit: 'Edit',
    lvEnd: 'End for everyone',
    lvCancelClass: 'Cancel class',
    lvCancelConfirm: 'Cancel this class? Learners will no longer see it.',
    lvCancelSeries: 'Also cancel the later classes in this weekly series?',
    lvEndConfirm: 'End the class for everyone now?',
    lvAttendance: 'Attendance',
    lvRecordingsN: 'Recordings ({n})',
    lvA_attended: 'Attended',
    lvA_avg: 'Average time',
    lvA_chats: 'Chat messages',
    lvA_polls: 'Poll answers',
    lvA_hands: 'Raised hands',
    lvA_pending: 'Zoom reports who was in the meeting while the class runs; until then only Join clicks are shown.',
    lvA_learner: 'Learner',
    lvA_status: 'Status',
    lvA_minutes: 'Minutes',
    lvA_talk: 'Talk (min)',
    lvModerator: 'host',
    lvPresent: 'Present',
    lvJoinedBriefly: 'Joined briefly',
    lvAbsent: 'Absent',
    lvCsv: 'Download CSV',
    lvNoRecordingsYet: 'No recording yet. Add the link to your recording (YouTube unlisted, Google Drive…) below.',
    lvRecordingOff: 'Recording was switched off for this class.',
    lvRec_processing: 'Processing',
    lvRec_draft: 'Not published',
    lvRec_published: 'Published',
    lvRec_deleted: 'Deleted',
    lvPreview: 'Preview',
    lvPublish: 'Publish to learners',
    lvUnpublish: 'Unpublish',
    lvDelete: 'Delete',
    lvDeleteRecConfirm: 'Delete this recording permanently?',
    lvNoCaptions: 'No captions yet.',
    lvCaptionLang: 'Captions language',
    lvTranslateTo: 'Also translate into',
    lvUploadCaptions: 'Upload captions',
    lvCaptionsHelp: 'A VTT or SRT file (for example exported from YouTube or a transcription tool). It also becomes the transcript.',
    lvCaptionsDone: 'Captions added.',
    lvCaptionsTranslating: 'Captions added; translating into {n} languages (a few minutes).',
    lvR_held: 'Classes held',
    lvR_upcoming: 'Upcoming',
    lvR_rate: 'Attendance rate',
    lvR_enrolled: 'of {n} enrolled learners',
    lvR_when: 'When',
    lvR_recs: 'Recordings',
    lvR_askAi: 'Tip: ask Learning analytics about live class attendance, e.g. "Who attended fewer than half of the live classes?"',
    lvFreeLimit: 'The free Zoom plan ends meetings after {n} minutes; learners can rejoin with the same link.',
    lvFreeNote: 'Free Zoom plan: meetings last up to 40 minutes, recordings are saved on the host\'s computer, and polls need a paid plan (use chat or reactions instead). Breakout rooms, screen sharing, whiteboard and the waiting room work.',
    lvRecUrl: 'Recording link',
    lvRecMinutes: 'Length (min)',
    lvAddRecording: 'Add recording',
    lvCheckCloud: 'Fetch Zoom cloud recording',
    lvRecFreeHelp: 'Upload the recording from your computer to YouTube (unlisted) or Google Drive (anyone with the link), paste the link here, then publish it.',
    lvRecPaidHelp: 'Zoom cloud recordings are added here automatically a few minutes after the class; you can also add other links.',
    lvZoomMeeting: 'Zoom meeting {id}',
    lvPasscode: 'passcode {code}',
    lvRecPasscode: 'Passcode: {code}',
    lvCaptionsLabel: 'Captions:',
    'lvZoomError_not-configured': 'Zoom is not connected: the meeting will be created when Zoom is set up.',
    lvZoomError_auth: 'Zoom refused the app keys: check the Server-to-Server OAuth app.',
    lvZoomError_unreachable: 'Zoom could not be reached: the meeting will be created when you start the class.',
    lvZoomError_plan: 'This needs a paid Zoom plan.',
    lvZoomError_error: 'The Zoom meeting could not be created: it will be retried when you start the class.',
    lvErr_url: 'Enter a full https:// link.',
    lvErr_plan: 'This needs a paid Zoom plan.',
    aiTitle: 'Learn with AI',
    aiSubtitle: 'Smart help at every step of your exam preparation, in Tamil or English.',
    aiTutorTitle: 'AI tutor in every course',
    aiTutorText: 'Ask questions any time. It explains ideas in simple words, shows where a topic is covered and checks your progress, using your course’s own lessons.',
    aiPathTitle: 'A learning path made for you',
    aiPathText: 'A short diagnostic test finds what you already know. Your path then focuses on the concepts you still need to master.',
    aiLanguageTitle: 'Lessons in Tamil',
    aiLanguageText: 'English course lessons and video captions are translated into Tamil by AI, with one click to see the original.',
    aiAssistantTitle: 'Help whenever you need it',
    aiAssistantText: 'Our AI assistant answers your questions about courses, certificates and using ThervuMitra, on every page.',
    aiTeachTitle: 'AI for teachers',
    aiTeachText: 'Build a course outline with the AI course builder, and ask about your learners’ progress in plain language.',
    aiNote: 'AI answers can be wrong. Check the course material for anything important.',
    dashTabsLabel: 'Dashboard sections',
    dashWelcome: 'Welcome back, {name}!',
    dashBandTitle: 'My learning',
    dashBandText: 'Your courses, mock tests and progress, all in one place.',
    dashTabCourses: 'My courses',
    dashTabGamification: 'Gamification',
    dashTabLive: 'Live classes',
    lvDashEmpty: 'No live classes coming up. When your courses schedule one, it will appear here.',
    // Course list search and status filter (thervumitra/DashboardCourseFilters.jsx).
    dashSearchLabel: 'Search your courses by name, institution or course number',
    dashSearchPlaceholder: 'Search your courses',
    dashSearchClear: 'Clear search',
    dashStatusLabel: 'Course status',
    dashStatusAll: 'All',
    dashStatusInProgress: 'In progress',
    dashStatusNotStarted: 'Not started',
    dashStatusDone: 'Done',
    dashNoMatches: 'No courses match your search.',
    dashNoMatchesText: 'Try another word, or show all your courses.',
    dashNoStatus: 'No courses with this status yet.',
    dashShowAll: 'Show all courses',
    vdNav: 'Vidya AI',
    vdTitle: 'Vidya',
    vdTagline: 'Your AI tutor for TRB exams',
    vdHeroTitle: 'Your personal AI tutor for every Tamil Nadu teacher exam.',
    vdHeroText: 'Vidya explains every unit of your syllabus, solves problems step by step, writes practice questions and timed mock tests, checks your answers and lesson plans, and remembers which exam you are preparing for. In Tamil or English.',
    vdSeePlans: 'See plans',
    vdOpenVidya: 'Open Vidya',
    vdBuiltFor: 'Built for',
    vdResearch: 'Research',
    vdFeaturesTitle: 'One tutor, many ways to learn',
    vdFeat_ask: 'Ask anything',
    vdFeatText_ask: 'Clear explanations at your level, from basics to advanced, with examples and quick checks of understanding.',
    vdFeat_solve: 'Solve step by step',
    vdFeatText_solve: 'Worked solutions with every step, the formula and why it applies, a check of the answer and the common trap.',
    vdFeat_quiz: 'Practice quizzes',
    vdFeatText_quiz: 'Exam-style questions on any topic and difficulty, graded instantly with explanations.',
    vdFeat_research: 'Research with sources',
    vdFeatText_research: 'Structured reports from the web with citations, perspectives and the latest data.',
    vdFeat_visualize: 'Visualize',
    vdFeatText_visualize: 'Mind maps, flowcharts, timelines and charts that make hard topics easy to remember.',
    vdFeat_notebook: 'Notebook',
    vdFeatText_notebook: 'Save answers, solutions and diagrams to revise later.',
    vdFeat_mistakes: 'Mistake bank',
    vdFeatText_mistakes: 'Every wrong answer is saved and comes back for review until you master it.',
    vdFeat_profile: 'Remembers you',
    vdFeatText_profile: 'Your exam, goals and weak topics shape every answer. You can see and edit what Vidya remembers.',
    vdHowTitle: 'How it works',
    vdHow1: 'Tell Vidya your goal',
    vdHowText1: 'Your exam, date and level, once. Vidya plans around them.',
    vdHow2: 'Learn your way',
    vdHowText2: 'Ask, solve, quiz, research or visualize, and watch each step Vidya takes.',
    vdHow3: 'Fix your gaps',
    vdHowText3: 'Mistakes come back for review and weak topics get more practice.',
    vdPlansTitle: 'Vidya AI plans',
    vdExtendTitle: 'Extend your Vidya AI plan',
    vdPlansText: 'One payment, no auto-renewal. Buy again any time to extend from your current end date.',
    vdPlanMonth: '1 month',
    vdPlanYear: '1 year',
    vdPerMonth: 'per month',
    vdPerYear: 'per year',
    vdPerMonthEquiv: 'Just ₹{price} a month',
    vdSave_pct: 'Save {pct}%',
    vdPlanAll: 'All modes: ask, solve, quiz, research, visualize',
    vdPlanFair: 'About 150 messages a day (fair use)',
    vdPlanGst: 'Price includes GST, with a tax invoice',
    vdGetPass: 'Get this plan',
    vdExtendPass: 'Extend plan',
    vdNoPlans: 'Vidya passes are not on sale right now. Please check back soon.',
    vdPaymentsOff: 'Online payments are being set up. Please try again later.',
    vdStat1: 'Answers in Tamil and English',
    vdStat2: 'Step-by-step solutions',
    vdStat3: 'Sources for every fact',
    vdHowEyebrow: 'Three steps',
    vdPlansEyebrow: 'Plans',
    vdForDays: 'for {n} days',
    vdTrust: 'GST included with a tax invoice. Secure payment. No auto-renewal.',
    vdFinalTitle: 'Start studying with Vidya today',
    vdFinalText: 'Your AI tutor for PG TRB, TN TET, UGC NET and every TRB exam, in Tamil or English, any time of day.',
    vdCouldNotAdd: 'We could not add the plan to your cart. Please try again.',
    vdFaqTitle: 'Questions',
    vdFaqQ1: 'Which exams and subjects does Vidya cover?',
    vdFaqA1: 'The ten exams ThervuMitra prepares you for: PG TRB, UG TRB, TN TET, SGT, TRB PET & Special Education, BEO, Polytechnic TRB, UGC NET, TN SET and CSIR NET, and every subject they test.',
    vdFaqQ2: 'Does it work in Tamil?',
    vdFaqA2: 'Yes. Ask in Tamil or English and Vidya answers in that language, or choose an answer language in your profile. Technical terms come with their English names.',
    vdFaqQ3: 'What does fair use mean?',
    vdFaqA3: 'Vidya includes about 150 messages a day, whether it comes with your course or a plan; research and long quizzes count a little more. The allowance resets every day.',
    vdFaqQ4: 'Will my Vidya AI plan renew automatically?',
    vdFaqA4: 'No. A plan runs for its period and ends. Buy another plan whenever you like; it starts when your current one ends. If you buy a paid course, Vidya AI comes with it and you need no plan.',
    vdFaqQ5: 'Can I trust the answers?',
    vdFaqA5: 'Vidya is very capable but can make mistakes. For facts that change it searches the web and shows sources. Check anything important against your textbooks or official sources.',
    vdPreviewQ: 'Explain Article 21 with a quick quiz',
    vdPreviewTitle: 'Article 21: Protection of life and personal liberty',
    vdPreviewA: 'No person shall be deprived of life or personal liberty except by procedure established by law. The Supreme Court has read it widely…',
    vdToday: 'Today',
    vdGreeting_morning: 'Good morning',
    vdGreeting_afternoon: 'Good afternoon',
    vdGreeting_evening: 'Good evening',
    vdCountdown: '{exam} · {n} days',
    vdStreak: '🔥 {n} days',
    vdStreakOne: '🔥 1 day',
    vdStreakRisk: 'Study today to keep your {n}-day streak',
    vdTodayTasks: "Today's tasks",
    vdTodayEmpty: 'Nothing planned for this day.',
    vdTodayProgress: 'Progress today',
    vdTodayProgressText: '{done}/{total} tasks · {minutes}/{planned} min',
    vdTaskStart: 'Start',
    vdTaskSkip: 'Skip',
    vdTaskMarkDone: 'Mark as done',
    vdTaskKind_quiz: 'Quiz',
    vdTaskKind_mock: 'Mock test',
    vdTaskKind_evaluate: 'Answer writing',
    vdTaskKind_study: 'Study',
    vdTaskKind_review: 'Review mistakes',
    vdMinutes: '{n} min',
    vdThisWeek: 'This week',
    vdPlanTitle: 'Plan',
    vdDailyTime: 'Daily study time',
    vdReplan: 'Re-plan my week',
    vdPlanning: 'Planning your week…',
    vdPlanFallback: "Basic plan — Vidya couldn't build a personal one just now.",
    vdPlanErr_limit: 'You have re-planned several times today. Please try again tomorrow.',
    vdPlanErr_allowance: "You've used today's Vidya allowance. Try again tomorrow.",
    vdPlanErr_unavailable: 'Vidya could not plan your week just now. Please try again.',
    vdPlanErr_profile: 'Could not load your profile, so nothing was changed. Please try again.',
    vdPlanLoadErr: 'Could not load your plan just now.',
    vdRetry: 'Try again',
    vdSetupHelp: 'Tell Vidya about your exam and how long you can study each day. It will plan your next 7 days.',
    vdMakePlan: 'Make my plan',
    vdNewChat: 'New chat',
    vdChats: 'Chats',
    vdNotebook: 'Notebook',
    vdMistakeBank: 'Mistake bank',
    vdMyProfile: 'My profile',
    vdMenu: 'Menu',
    vdSearchChats: 'Search chats',
    vdNoChats: 'No chats yet.',
    vdUntitled: 'Untitled chat',
    vdPin: 'Pin chat',
    vdUnpin: 'Unpin chat',
    vdDelete: 'Delete',
    vdDeleteChatConfirm: 'Delete this chat?',
    vdPassUntil: 'Plan active until {date}',
    vdDueN: '{n} due for review',
    vdMode: 'Mode',
    vdMode_ask: 'Ask',
    vdMode_solve: 'Solve',
    vdMode_quiz: 'Quiz',
    vdMode_research: 'Research',
    vdMode_visualize: 'Visualize',
    vdModeTitle_ask: 'Ask Vidya anything',
    vdModeTitle_solve: 'Solve a problem step by step',
    vdModeTitle_quiz: 'Practice with a quiz',
    vdModeTitle_research: 'Research a topic',
    vdModeTitle_visualize: 'See it visually',
    vdModeHelp_ask: 'Concepts, doubts, summaries, comparisons: explained at your level.',
    vdModeHelp_solve: 'Paste a question from maths, physics, chemistry or statistics for a complete worked solution.',
    vdModeHelp_quiz: 'Name a topic and get exam-style questions, graded instantly. Wrong answers go to your mistake bank.',
    vdModeHelp_research: 'Vidya searches the web and writes a structured report with sources.',
    vdModeHelp_visualize: 'Mind maps, flowcharts, timelines and charts for any topic.',
    vdPlaceholder_ask: 'Ask a question or describe what you want to learn…',
    vdPlaceholder_solve: 'Type or paste the problem…',
    vdPlaceholder_quiz: 'Topic for the quiz, e.g. "Child development: Piaget"…',
    vdPlaceholder_research: 'What should Vidya research?',
    vdPlaceholder_visualize: 'What should Vidya draw? e.g. "Mind map of Sangam literature"…',
    vdStarter_ask1: 'Explain Piaget\'s stages of cognitive development with classroom examples',
    vdStarter_ask2: 'Make me a study plan for my exam date',
    vdStarter_ask3: 'What is the difference between formative and summative assessment?',
    vdStarter_ask4: 'Explain the five Thinais of Sangam literature',
    vdStarter_solve1: 'A train 240 m long crosses a pole in 12 s. Find its speed in km/h.',
    vdStarter_solve2: 'Solve: x² − 5x + 6 = 0, and explain the method',
    vdStarter_solve3: 'Find the pH of a 0.01 M HCl solution',
    vdStarter_solve4: 'Find the mean, median and mode of: 4, 7, 7, 9, 13',
    vdStarter_quiz1: 'Child development and pedagogy',
    vdStarter_quiz2: 'Tamil grammar for the Tamil eligibility test',
    vdStarter_quiz3: 'UGC NET Paper I: research aptitude',
    vdStarter_quiz4: 'Revise my weak topics',
    vdStarter_research1: 'Latest Tamil Nadu school education schemes',
    vdStarter_research2: 'What the National Education Policy 2020 says about teacher education',
    vdStarter_research3: 'Recent research on inclusive education in Indian classrooms',
    vdStarter_research4: 'Right to Education Act: key provisions for teachers',
    vdStarter_visualize1: 'Mind map of learning theories',
    vdStarter_visualize2: 'Flowchart of photosynthesis',
    vdStarter_visualize3: 'Timeline of Tamil literature from Sangam to modern times',
    vdStarter_visualize4: 'Chart of Kohlberg\'s stages of moral development',
    vdQuestions: 'Questions',
    vdDifficulty: 'Difficulty',
    vdExamStyle: 'Exam style',
    vdLevel_mixed: 'Mixed',
    vdLevel_easy: 'Easy',
    vdLevel_medium: 'Medium',
    vdLevel_hard: 'Hard',
    vdSend: 'Send',
    vdStop: 'Stop',
    vdDisclaimer: 'Vidya can make mistakes. Check important facts with official sources.',
    vdAttachPhoto: 'Add a photo',
    vdPhotoMax: 'You can add up to 3 photos.',
    vdPhotoFailed: 'A photo did not upload. Tap it to try again, or remove it.',
    vdPhotoDecode: 'We could not read that photo.',
    vdPhotoTooLarge: 'That photo is too large.',
    vdPhotoDaily: 'You have reached the daily photo limit for today.',
    vdPhotoRemove: 'Remove photo',
    vdPhotoOpen: 'Open photo',
    vdStarterSnap: 'Snap a photo of a problem',
    vdThinking: 'Vidya is thinking…',
    vdSaveToNotebook: 'Save to notebook',
    vdSavedToNotebook: 'Saved to your notebook',
    vdCouldNotSave: 'Could not save. Please try again.',
    vdCopy: 'Copy',
    vdCopied: 'Copied',
    vdNote: 'Note',
    vdActivity: 'Activity',
    vdActivityEmpty: 'When Vidya works on an answer, each step appears here: thinking, searching, building quizzes and drawing.',
    vdStarting: 'Starting…',
    vdSources: 'Sources',
    vdUsage: '{pct}% of today’s fair-use allowance used',
    vdTool_think: 'Thinking it through',
    vdTool_web_search: 'Searching the web',
    vdTool_show_quiz: 'Building a quiz',
    vdTool_show_flashcards: 'Making flashcards',
    vdTool_show_chart: 'Drawing a chart',
    vdTool_show_diagram: 'Drawing a diagram',
    vdTool_remember: 'Updating your profile',
    vdTool_get_my_mistakes: 'Checking your mistake bank',
    vdTool_show_evaluation: 'Marking your answer',
    vdFeat_evaluate: 'Answer evaluation',
    vdFeatText_evaluate: 'Write an answer or a lesson plan, or photograph it, and get marks, a rubric, what to fix and a model answer.',
    vdMode_evaluate: 'Evaluate',
    vdModeTitle_evaluate: 'Get your answer marked',
    vdModeHelp_evaluate: 'Paste or photograph an answer or a lesson plan and get marks, feedback and a model answer.',
    vdPlaceholder_evaluate: 'Paste or photograph the question and your answer or lesson plan (up to 3 pages)',
    vdStarter_evaluate1: 'Give me a 10-mark question on educational psychology to practise',
    vdStarter_evaluate3: 'Evaluate my answer (I will paste it)',
    vdStarterEvalPhoto: 'Evaluate my handwritten answer',
    vdPreset: 'Paper',
    vdPreset_other: 'Other',
    vdMaxMarks: 'Max marks',
    vdWordLimit: 'Word limit',
    vdBand_NeedsWork: 'Needs work',
    vdBand_Average: 'Average',
    vdBand_Good: 'Good',
    vdBand_Excellent: 'Excellent',
    vdEvalEstimate: 'AI estimate. Real examiners vary.',
    vdEvalNotSaved: 'Not saved',
    vdEvalWords: 'Words',
    vdEvalOverLimit: 'over the limit',
    vdEvalStrengths: 'Strengths',
    vdEvalImprove: 'How to improve',
    vdEvalMissing: 'Missing points',
    vdEvalModel: 'Show model answer',
    vdEvalModelHide: 'Hide model answer',
    vdEvalTryAgain: 'Try this question again',
    vdMyAnswers: 'My answers',
    vdAnswersEmpty: 'No evaluated answers yet.',
    vdAnswersStart: 'Evaluate an answer',
    vdAnswersDelete: 'Delete this evaluation?',
    vdAnswersDeleted: 'Conversation deleted',
    vdAnswersAll: 'All papers',
    vdAnswersCount: '{n} answers',
    vdAnswersAvg: 'average {pct}%',
    vdAnswersRecent: 'Last scores',
    vdMockTests: 'Mock tests',
    vdMockIntro: 'Timed, exam-style tests with negative marking, then a full analysis of where you lose marks.',
    vdMockShared: 'Shared',
    vdMockMine: 'Mine',
    vdMockNoShared: 'No exam papers yet. Make a mock of your own in the meantime.',
    vdMockNoMine: 'No mocks yet. Make one on any topic.',
    vdMockLoadFailed: 'Could not load mock tests.',
    vdMockRetry: 'Try again',
    vdMockQuestionsN: '{n} questions',
    vdMockMinutes: '{n} min',
    vdMockTakers: '{n} takers',
    vdMockBest: 'Best: {score} / {max}',
    vdMockStart: 'Start',
    vdMockResume: 'Resume',
    vdMockRetake: 'Retake (practice)',
    vdMockAiMade: 'AI-made, not reviewed',
    vdMockMaking: 'Making your mock… {done}/{total}',
    vdMockGenError: 'Vidya could not finish this mock: {done} of {total} questions are ready.',
    vdMockRetryGen: 'Try again',
    vdMockFailed: "Couldn't make this mock.",
    vdMockWaitMaking: 'You can make another mock once this one is ready.',
    vdMockMake: 'Make a mock',
    vdMockTopic: 'Topic',
    vdMockTopicPh: 'e.g. Educational psychology: learning theories',
    vdMockCount: 'Questions (10–50)',
    vdMockPreset: 'Marking',
    vdMockPreset_default: 'My exam\'s pattern',
    vdMockMakeHelp: 'One minute per question. You can make up to 3 mocks a day.',
    vdMockCreate: 'Make my mock',
    vdMockCreating: 'Starting…',
    vdMockCancel: 'Cancel',
    vdMockForTask: 'For your plan: {topic}',
    vdMockSuggested: 'This exam paper matches your exam. It counts toward your plan.',
    vdMockForTaskMake: 'Make a mock on this topic below. It counts toward your plan.',
    vdMockMakeInstead: 'Make a mock on {topic} instead',
    vdMockErr_limit: 'You can make 3 mocks a day. Please try again tomorrow.',
    vdMockErr_vidyaLimit: "You've reached Vidya's limit for now. Try again later.",
    vdMockErr_allowance: "You've used today's Vidya allowance. Try again tomorrow.",
    vdMockErr_busy: 'Vidya is still making your other mock. Please wait for it to finish.',
    vdMockErr_closed: 'Time was up for that test, so it has been submitted.',
    vdMockErr_empty: 'This mock has no questions yet.',
    vdMockErr_generic: 'Something went wrong. Please try again.',
    vdMockLoading: 'Opening your test…',
    vdMockTimeLeft: 'Time left',
    vdMockFiveLeft: '5 minutes left.',
    vdMockSave_saved: 'Saved',
    vdMockSave_saving: 'Saving…',
    vdMockSave_failed: 'Not saved yet, retrying',
    vdMockSubmit: 'Submit',
    vdMockLeave: 'Leave',
    vdMockLeaveConfirm: 'Leave the test? The timer keeps running, and you can resume until time is up.',
    vdMockSections: 'Sections',
    vdMockSectionN: 'Section {n}',
    vdMockQuestionN: 'Question {n} of {total}',
    vdMockOptions: 'Options',
    vdMockMark: 'Mark for review',
    vdMockClear: 'Clear',
    vdMockPrevious: 'Previous',
    vdMockSaveNext: 'Save & next',
    vdMockKeys: 'Keys: 1–4 or A–D to choose, N next, P previous, M mark',
    vdMockPalette: 'Questions',
    vdMockClose: 'Close',
    vdMockAnsweredOf: '{n}/{total} answered',
    vdMockPaletteItem: 'Question {n}: {state}',
    vdMockState_notVisited: 'Not visited',
    vdMockState_notAnswered: 'Not answered',
    vdMockState_answered: 'Answered',
    vdMockState_marked: 'Marked for review',
    vdMockState_answeredMarked: 'Answered and marked',
    vdMockSubmitTitle: 'Submit the test?',
    vdMockUnanswered: '{n} questions are unanswered.',
    vdMockUnansweredOne: '1 question is unanswered.',
    vdMockAllAnswered: 'You have answered every question.',
    vdMockKeepGoing: 'Keep going',
    vdMockSubmitNow: 'Submit now',
    vdMockSubmitting: 'Submitting…',
    vdMockSubmitFailed: 'Your test could not be submitted. Check your connection.',
    vdMockResults: 'Results',
    vdMockPractice: 'Practice attempt',
    vdMockFewTakers: 'Not enough takers yet',
    vdMockBetter: 'Better than {pct}% of {n} takers',
    vdMockCorrect: 'Correct',
    vdMockWrong: 'Wrong',
    vdMockSkipped: 'Skipped',
    vdMockAccuracy: 'Accuracy',
    vdMockColSection: 'Section',
    vdMockColAttempted: 'Attempted',
    vdMockColScore: 'Score',
    vdMockColAvg: 'Avg time',
    vdMockSlowest: 'Slowest questions',
    vdMockWrongTime: 'Time spent on wrong answers: {time}',
    vdMockSolutions: 'Solutions',
    vdMockFilter_all: 'All',
    vdMockFilter_wrong: 'Wrong',
    vdMockFilter_skipped: 'Skipped',
    vdMockFilter_marked: 'Marked',
    vdMockNone: 'No questions here.',
    vdMockQ: 'Q{n}',
    vdMockOutcome_right: 'Correct',
    vdMockOutcome_wrong: 'Wrong',
    vdMockOutcome_skipped: 'Skipped',
    vdMockMarkedTag: 'Marked',
    vdMockTime: 'Time: {time}',
    vdMockYourAnswer: 'Your answer',
    vdMockCorrectAnswer: 'Correct answer',
    vdMockNotAnswered: 'You did not answer this question.',
    vdMockAskVidya: 'Ask Vidya',
    vdMockAskPrompt: 'Explain this question: {stem} Options: {options}. I chose {choice}; the answer is {answer}.',
    vdMockAskSkipped: 'Explain this question: {stem} Options: {options}. I did not answer it; the answer is {answer}.',
    vdMockBackToList: 'Back to mock tests',
    vdMockBackToPlan: 'Back to Today',
    // Studio's mock editor (shared papers).
    vdMockCheckAnswer: 'Check the answer',
    vdMockChecked: "I've checked",
    vdState_running: 'In progress',
    vdState_done: 'Done',
    vdState_error: 'Failed',
    vdError_limit: 'You are sending messages very quickly. Please wait a little and try again.',
    vdError_budget: 'Vidya is very busy right now. Please try again in a while.',
    vdError_allowance: 'You have used today’s fair-use allowance. It resets tomorrow.',
    vdError_auth: 'Your session has expired. Please reload the page.',
    vdError_unavailable: 'Vidya is not available right now. Please try again in a little while.',
    vdError_invalid: 'That message could not be sent. Please shorten it and try again.',
    vdQuiz: 'Quiz',
    vdQuestionsN: '{n} questions',
    vdYourAnswer: 'Your answer',
    vdModelAnswer: 'Model answer:',
    vdMarkYourself: 'Did you get it right?',
    vdIGotIt: 'I got it',
    vdIMissedIt: 'I missed it',
    vdCorrect: 'Correct!',
    vdAnswerWas: 'Answer: {answer}',
    vdMyAnswer: 'My answer: {given}',
    vdAnsweredN: '{n} of {total} answered',
    vdCheckAnswers: 'Check answers',
    vdScore: 'Score: {score}/{total}',
    vdAddedToBank: '{n} added to your mistake bank',
    vdExplainMistakes: 'Explain my mistakes',
    vdExplainMistakesPrompt: 'I took the quiz on {topic}. Explain where I went wrong in these questions and the concepts I should revise:',
    vdAnotherQuiz: 'Another quiz',
    vdAnotherQuizPrompt: 'Give me another quiz on {topic}, a little harder.',
    vdFlashcards: 'Flashcards',
    vdCardOf: '{n} of {total}',
    vdFlip: 'Flip card',
    vdPrev: 'Previous',
    vdNext: 'Next',
    vdShuffle: 'Shuffle',
    vdChart: 'Chart',
    vdDiagram: 'Diagram',
    vdDrawing: 'Drawing…',
    vdCouldNotDraw: 'This chart could not be drawn.',
    vdNotebookHelp: 'Answers, solutions and diagrams you saved, to revise any time.',
    vdSearchNotes: 'Search notes',
    vdNoNotes: 'Nothing saved yet. Use "Save to notebook" under any answer.',
    vdDeleteNoteConfirm: 'Delete this note?',
    vdAskFollowUp: 'Ask a follow-up',
    vdAskAboutNote: 'About my note "{title}": help me understand it better.',
    vdQuizMe: 'Quiz me on this',
    vdQuizMeOnNote: 'Quiz me on my note "{title}".',
    vdMistakeHelp: 'Questions you got wrong come back for review: right answers move them further out, wrong ones bring them back sooner.',
    vdDueToday: 'Due today',
    vdOpenMistakes: 'To master',
    vdMastered: 'Mastered',
    vdReviewDue: 'Review {n} now',
    vdPracticeWeak: 'Practise my weak topics',
    vdPracticeWeakPrompt: 'Give me a revision quiz on my weak topics: {topics}.',
    vdNoMistakes: 'No mistakes saved yet. Take a quiz and any wrong answers will appear here.',
    vdNextReview: 'next review {date}',
    vdRemove: 'Remove',
    vdOther: 'Other',
    vdShowAnswer: 'Show answer',
    vdYouAnsweredBefore: 'Earlier you answered: {given}',
    vdReviewDone: 'Review complete',
    vdReviewScore: 'You got {n} of {total} right.',
    vdBackToBank: 'Back to mistake bank',
    vdProfileHelp: 'Vidya uses this to tailor every answer. Only you and Vidya can see it.',
    vdStatQuestions: 'Questions asked',
    vdStatQuizzes: 'Quizzes taken',
    vdStatAccuracy: 'Quiz accuracy',
    vdStatDays: 'Active days',
    vdExam: 'Target exam',
    vdExamDate: 'Exam date',
    vdLevel: 'Level',
    vdLevelName_beginner: 'Beginner',
    vdLevelName_intermediate: 'Intermediate',
    vdLevelName_advanced: 'Advanced',
    vdStyle: 'Answer style',
    vdStyle_concise: 'Short and to the point',
    vdStyle_detailed: 'Detailed',
    vdStyle_socratic: 'Guide me with questions',
    vdAnswerLanguage: 'Answer language',
    vdSiteLanguage: 'Same as the site',
    vdNotSet: 'Not set',
    vdGoals: 'Goals',
    vdGoalsPlaceholder: 'e.g. Clear PG TRB Tamil in January 2027; strengthen grammar and Sangam literature.',
    vdSave: 'Save',
    vdSaved: 'Saved',
    vdMemory: 'What Vidya remembers about you',
    vdMemoryHelp: 'Vidya notes lasting things you share, like goals and topics you find hard. Remove anything you like.',
    vdNoMemory: 'Nothing yet. Vidya will remember things like your goals as you chat.',
    vdForget: 'Forget',
    vdFact_goal: 'Goal',
    vdFact_preference: 'Preference',
    vdFact_fact: 'About you',
    vdFact_struggle: 'Finds hard',
    vdFact_strength: 'Strong in',
    vdWeakTopics: 'Weak topics (from your quizzes)',
    vdWeakCounts: '{misses} wrong · {hits} right',
    // Course about page (thervumitra/course-about).
    caCourses: 'Courses',
    caOverview: 'Overview',
    caDescription: 'Course description',
    caLearn: "What you'll learn",
    caRequirements: 'Requirements',
    caCurriculum: 'Curriculum',
    caInstructors: 'Instructors',
    caFaq: 'FAQs',
    caFaqTitle: 'Frequently asked questions',
    caReviews: 'Reviews',
    caSectionsNav: 'Course sections',
    caSections: '{count} sections',
    caLessons: '{count} lessons',
    caIncludes: 'This course includes',
    caOfferedBy: 'Offered by',
    caLevel: 'Level',
    caLanguage: 'Language',
    caStarts: 'Starts',
    caEnds: 'Ends',
    caPacing: 'Pace',
    caSelfPaced: 'Self-paced',
    caInstructorLed: 'Instructor-led',
    caEffort: 'Effort',
    caCourseCode: 'Course code',
    caTopics: 'Topics',
    caEnrolment: 'Enrolment',
    caShare: 'Share this course',
    caEmail: 'Email',
    caCopyLink: 'Copy link',
    caCopied: 'Link copied',
    caEditInStudio: 'Edit in Studio',
    // Delete course in Studio home (thervumitra/DeleteCourse.jsx).
    dcMenu: 'Delete course',
    dcTitle: 'Delete this course?',
    dcWarning: 'This permanently deletes the course run. It cannot be undone. It removes:',
    dcContent: 'All course content: sections, units, problems and settings',
    dcAssets: 'Uploaded files (images, PDFs, videos) in Files & uploads',
    dcSearch: 'The course from the catalog, course search and Studio search',
    dcTeam: 'The Studio course team (staff and admin access)',
    dcKept: 'Enrolments, grades, certificates and orders are kept for the records.',
    dcTypeLabel: 'To confirm, type the course ID',
    dcCancel: 'Cancel',
    dcConfirm: 'Delete course',
    dcDeleting: 'Deleting…',
    dcDoneTitle: 'Course deleted',
    dcDone: '"{name}" has been deleted.',
    dcAssetsKept: 'Its files were kept because other runs of this course use them: {runs}.',
    dcClose: 'Close',
    dcErrorGeneric: 'The course could not be deleted. Please try again.',
    dcError_forbidden: 'Only platform administrators can delete courses.',
    dcError_not_found: 'This course no longer exists.',
    dcError_not_confirmed: 'The course ID you typed does not match.',
    // Studio: /authoring/mocks (thervumitra/mocks/MocksAdminPage.jsx). The "Check the
    // answer" badge and "I've checked" reuse vdMockCheckAnswer and vdMockChecked.
    mkAdmTitle: 'Mock papers',
    mkAdmIntro: 'Exam papers for Vidya\'s Mock tests. Vidya writes the questions; you check them, then publish.',
    mkAdmDenied: 'Only platform staff and the vidya_mocks group can manage mock papers.',
    mkAdmPapers: 'Papers',
    mkAdmNew: 'New paper',
    mkAdmNewIntro: 'Choose an exam preset or set up your own sections. Vidya writes the questions once you create the paper.',
    mkAdmNone: 'No mock papers yet',
    mkAdmNoneText: 'Create a paper and Vidya will write its questions for you to check.',
    mkAdmNoMatch: 'No papers match.',
    mkAdmSearch: 'Search by title or exam',
    mkAdmAll: 'All',
    mkAdmAllPapers: 'All mock papers',
    mkAdmOpen: 'Open',
    mkAdmCreated: 'Created {date}',
    mkAdmCol_title: 'Title',
    mkAdmCol_exam: 'Exam',
    mkAdmCol_preset: 'Preset',
    mkAdmCol_questions: 'Questions',
    mkAdmCol_status: 'Status',
    mkAdmCol_takers: 'Takers',
    mkAdmCol_avg: 'Average',
    mkAdmStatus_draft: 'Draft',
    mkAdmStatus_generating: 'Generating',
    mkAdmStatus_review: 'In review',
    mkAdmStatus_published: 'Published',
    mkAdmPreset_custom: 'Custom',
    mkAdmF_title: 'Title',
    mkAdmF_exam: 'Exam',
    mkAdmF_examPh: 'e.g. TN TET Paper II',
    mkAdmF_language: 'Language',
    mkAdmF_preset: 'Preset',
    mkAdmF_minutes: 'Time (minutes)',
    mkAdmF_right: 'Marks for a right answer',
    mkAdmF_wrong: 'Marks taken off for a wrong answer',
    mkAdmF_sections: 'Sections',
    mkAdmF_topicsPh: 'Topics to cover (optional)',
    mkAdmTotalN: '{n} questions in all',
    mkAdmSectionsHelpPreset: 'The preset sets the sections, time and marks. Add the topics each section should cover.',
    mkAdmSectionsHelpCustom: 'Up to 6 sections and 5–100 questions in all. Time 10–180 minutes, +0.25 to +4 for a right answer, 0 to 2 off for a wrong one.',
    mkAdmSectionN: 'Section {n}',
    mkAdmSectionNameN: 'Section {n} name',
    mkAdmSectionCountN: 'Section {n} questions',
    mkAdmSectionTopicsN: 'Section {n} topics',
    mkAdmRemoveSectionN: 'Remove section {n}',
    mkAdmAddSection: 'Add section',
    mkAdmBad_title: 'Give the paper a title.',
    mkAdmBad_exam: 'The exam name is too long.',
    mkAdmBad_topics: 'A section\'s topics are too long (400 characters at most).',
    mkAdmBad_sections: 'A paper has 1 to 6 sections.',
    mkAdmBad_sectionName: 'Give every section a name.',
    mkAdmBad_count: 'Every section needs at least 1 question.',
    mkAdmBad_total: 'A paper has 5 to 100 questions in all.',
    mkAdmBad_minutes: 'The time must be 10 to 180 minutes.',
    mkAdmBad_right: 'A right answer earns 0.25 to 4 marks.',
    mkAdmBad_wrong: 'A wrong answer loses 0 to 2 marks.',
    mkAdmBadQ_stem: 'Write the question.',
    mkAdmBadQ_options: 'Fill in all 4 options.',
    mkAdmBadQ_distinct: 'The 4 options must all be different.',
    mkAdmBadQ_explanation: 'Add an explanation.',
    mkAdmCreate: 'Create and generate',
    mkAdmCreating: 'Creating…',
    mkAdmCancel: 'Cancel',
    mkAdmSave: 'Save',
    mkAdmSaving: 'Saving…',
    mkAdmSaved: 'Saved',
    mkAdmTab_questions: 'Questions',
    mkAdmTab_results: 'Results',
    mkAdmTab_settings: 'Settings',
    mkAdmStatMinutes: 'Minutes',
    mkAdmStatMarks: 'Marks',
    mkAdmGenerating: 'Vidya is writing questions… {done}/{total}',
    mkAdmGenProgress: 'Generation progress',
    mkAdmGenError: 'Generation stopped: {error}',
    mkAdmGenErr_queue: 'The generation job could not be started. Try again.',
    mkAdmMissingN: '{n} questions are still missing.',
    mkAdmGenerateMissing: 'Generate the missing questions',
    mkAdmPublish: 'Publish',
    mkAdmPublished: 'Published. Learners can now take this paper in Vidya.',
    mkAdmUnpublish: 'Unpublish',
    mkAdmUnpublished: 'Unpublished. The paper is back in review.',
    mkAdmWhy_generating: 'Wait for generation to finish.',
    mkAdmWhy_incomplete: '{n} questions are missing.',
    mkAdmWhy_flagged: '{n} questions still need checking.',
    mkAdmShow: 'Show',
    mkAdmAllQuestions: 'All',
    mkAdmFlaggedN: '{n} to check',
    mkAdmFlagHelp: 'A second check by Vidya doubted this answer. Check it, fix it if needed, then tick "I\'ve checked".',
    mkAdmNoSections: 'This paper has no sections.',
    mkAdmNoQuestions: 'No questions in this section yet.',
    mkAdmNoFlagged: 'Nothing to check in this section.',
    mkAdmTopicsLine: 'Topics: {topics}',
    mkAdmSectionFull: 'This section has all its questions.',
    mkAdmSectionMissing: '{n} questions missing in this section.',
    mkAdmRegenerate: 'Regenerate this section',
    mkAdmRegenHelp: 'Vidya fills this section\'s missing questions. Delete questions first to have them rewritten.',
    mkAdmRegenFull: 'The section is full. Delete questions first to have them rewritten.',
    mkAdmAddQuestion: 'Add question',
    mkAdmNewQuestion: 'New question',
    mkAdmQN: 'Question {n}',
    mkAdmF_stem: 'Question',
    mkAdmF_options: 'Options (select the correct one)',
    mkAdmF_explanation: 'Explanation',
    mkAdmF_topic: 'Topic',
    mkAdmF_difficulty: 'Difficulty',
    mkAdmDiff_easy: 'Easy',
    mkAdmDiff_medium: 'Medium',
    mkAdmDiff_hard: 'Hard',
    mkAdmPreview: 'Preview',
    mkAdmEditText: 'Edit',
    mkAdmCorrectN: 'Option {letter} is correct',
    mkAdmOptionN: 'Option {letter}',
    mkAdmAnswerFrozen: 'The paper is published, so the correct answer cannot change. You can still fix typos.',
    mkAdmDelete: 'Delete',
    mkAdmDeleteQConfirm: 'Delete this question?',
    mkAdmMoveUp: 'Move up',
    mkAdmMoveDown: 'Move down',
    mkAdmMoveFailed: 'The order could not be changed.',
    mkAdmSettings: 'Paper settings',
    mkAdmSettingsNote: 'Fewer sections than the paper\'s questions use cannot be saved; delete those questions first.',
    mkAdmSettingsPublished: 'Only the title and exam can change on a published paper.',
    mkAdmFrozen: 'This paper is published: its sections, time, marks and language are fixed.',
    mkAdmDeletePaper: 'Delete paper',
    mkAdmDeletePaperNote: 'Deletes the paper and its questions. Not possible once someone has submitted it.',
    mkAdmDeletePaperConfirm: 'Delete this paper and all its questions?',
    mkAdmNoTakers: 'No takers yet',
    mkAdmNoTakersText: 'Results appear here once learners submit this paper. Only first attempts count.',
    mkAdmMaxScore: 'Maximum score',
    mkAdmOutOf: 'out of {max}',
    mkAdmSpread: 'Score spread',
    mkAdmSpreadNote: 'Takers by score, as a share of the maximum.',
    mkAdmSpreadBar: '{n} takers scored {from}–{to}%',
    mkAdmHardest: 'Hardest questions',
    mkAdmHardestNote: 'The 10 questions most often answered wrong.',
    mkAdmNoHardest: 'No question has been answered wrong yet.',
    mkAdmWrongPct: '{pct}% wrong',
    mkAdmErr_generic: 'Something went wrong. Please try again.',
    mkAdmErr_invalid: 'That change could not be saved. Check the fields, and wait for any generation to finish.',
    mkAdmErr_published: 'The paper is published, so this cannot change.',
    mkAdmErr_incomplete: 'Every section needs all its questions, each complete, before publishing.',
    mkAdmErr_flagged: 'Some questions still need checking ("Check the answer").',
    mkAdmErr_taken: 'Learners have already submitted this paper.',
    mkAdmStuck: 'Generation seems stuck.',
    mkAdmOffline: 'Can\'t reach Studio, retrying',
    mkAdmLocked: 'Settings can be changed once generation finishes.',
    mkAdmUnsavedConfirm: 'Some questions have unsaved changes. Leave them unsaved?',
    mkAdmSectionOver: '{section} has {n} questions; it needs {need}. Delete the extra ones.',
    tagline: 'TRB தேர்வுக்கு உங்கள் நண்பன் · Your partner for every TRB exam.',
    heroMockTitle: 'TN TET Paper II · Mock test',
    heroMockQuestion: 'Question 46 of 150',
    heroVidyaName: 'Vidya',
    heroVidyaAnswer: 'பியாஜே: அறிவு வளர்ச்சியின் நான்கு நிலைகள்',
    heroRankTitle: 'Rank 12 of 4,500',
    heroRankText: 'TN TET mock test',
    statExams: 'TRB-family exams',
    statLanguagesTaEn: 'Tamil and English',
    medium_tamil: 'Tamil medium',
    medium_english: 'English medium',
    medium_bilingual: 'Bilingual',
    homeExamsTitle: 'Choose your exam',
    homeExamsText: 'Courses, mock tests and Vidya for ten Tamil Nadu teacher exams.',
    vdExamSubject: 'Subject',
    vdChooseSubject: 'Choose your subject',
    vdExamInvalid: 'Choose your subject for this exam.',
    vdMockPreset_tntet_p1: 'TN TET Paper I (+1, no negative)',
    mkAdmPreset_tntet_p1: 'TN TET Paper I',
    vdMockPreset_tntet_p2: 'TN TET Paper II (+1, no negative)',
    mkAdmPreset_tntet_p2: 'TN TET Paper II',
    vdMockPreset_pgtrb: 'PG TRB (+1, no negative)',
    mkAdmPreset_pgtrb: 'PG TRB',
    vdMockPreset_tamil_elig: 'Tamil eligibility test (+1, no negative)',
    mkAdmPreset_tamil_elig: 'Tamil eligibility test',
    vdMockPreset_ugtrb: 'UG TRB (BT Assistant) (+1, no negative)',
    mkAdmPreset_ugtrb: 'UG TRB (BT Assistant)',
    vdMockPreset_sgt: 'SGT (+1, no negative)',
    mkAdmPreset_sgt: 'SGT',
    vdMockPreset_beo: 'BEO (+1, no negative)',
    mkAdmPreset_beo: 'BEO',
    vdMockPreset_poly_trb: 'Polytechnic TRB (+1, no negative)',
    mkAdmPreset_poly_trb: 'Polytechnic TRB',
    vdMockPreset_pet_sped: 'TRB PET & Special Education (+1, no negative)',
    mkAdmPreset_pet_sped: 'TRB PET & Special Education',
    vdMockPreset_ugcnet: 'UGC NET (+2, no negative)',
    mkAdmPreset_ugcnet: 'UGC NET',
    vdMockPreset_tnset: 'TN SET (+2, no negative)',
    mkAdmPreset_tnset: 'TN SET',
    vdMockPreset_csirnet: 'CSIR NET (+2/+4, negative marks)',
    mkAdmPreset_csirnet: 'CSIR NET',
    vdPreset_short5: 'Concept explanation, 5 marks (100 words)',
    vdPreset_long10: 'Descriptive answer, 10 marks (250 words)',
    vdPreset_lesson_plan: 'Lesson plan, 25 marks (600 words)',
    vdPhase_Foundation: 'Foundation',
    vdPhase_Practice: 'Practice',
    vdPhase_Mock_sprint: 'Mock sprint',
    vdPhase_Final_revision: 'Final revision',
    vdStarter_evaluate2: 'Check my lesson plan for Class 6 Science (I will paste it)',
    mkAdmSectionMarks: 'Marks per part: {marks}',
    lhProgress: '{pct}% complete',
    lhCourseProgress: 'Course progress',
    lhStart: 'Start course',
    lhResume: 'Resume course',
    lhStartBlurb: 'Begin your course today',
    lhResumeBlurb: 'Pick up where you left off',
    lhCourses: 'Courses',
    lhHeaderNav: 'Main menu',
    lhCourseNav: 'Course menu',
    vdHowToGet: 'How to get Vidya AI',
    vdAccessEyebrow: 'Access',
    vdAccessTitle: 'Two ways to get Vidya AI',
    vdAccessBest: 'Recommended',
    vdAccessCourseTitle: 'Free with any paid course',
    vdAccessCourseText: 'Buy any paid ThervuMitra course and Vidya AI is included for as long as you have the course. No separate purchase.',
    vdBrowseCourses: 'Browse courses',
    vdAccessPlanTitle: 'No course? Get a Vidya AI plan',
    vdAccessPlanText: 'If you haven\'t bought a paid course, you can buy a Vidya AI plan on its own.',
    vdAccessPlanNotNeeded: 'Not needed: your course already includes Vidya AI.',
    vdIncludedWithCourse: 'Included with your course',
    vdIncludedNotice: 'Vidya AI is already included with your course. No plan needed.',
    vdPlansCourseNote: 'Buying any paid course also gives you Vidya AI.',
    vdFaqQ6: 'I bought a course. Do I need a Vidya AI plan?',
    vdFaqA6: 'No. Every paid course includes Vidya AI for as long as you have the course. Just open Vidya AI.',
    vdFaqQ7: 'I haven\'t bought a course. Can I still use Vidya AI?',
    vdFaqA7: 'Yes. Buy a Vidya AI plan on its own, or buy any paid course and get Vidya AI included.',
    shIncludedTitle: 'Included with this course',
    shIncludedOwned: 'Included with your course',
    shIncTutor: 'AI Tutor',
    shIncTutorText: 'Ask questions about this course\'s lessons any time and get step-by-step help.',
    shIncVidya: 'Vidya AI',
    shIncVidyaText: 'Your exam-prep assistant: mock tests, a study plan and answer evaluation for all your exams, for as long as you have this course. No separate plan needed.',
    shIncVidyaLink: 'About Vidya AI',
    shSkippedIncluded: 'Not charged: Vidya AI comes free with the course in your cart',
    vdClose: 'Close',
    exExams: 'Exams we prepare you for',
    exExamsText: 'Online coaching, mock tests and study plans for the Tamil Nadu teacher recruitment and eligibility exams.',
    exCoaching: '{exam} coaching',
    exPattern: '{exam} exam pattern',
    exSection: 'Section',
    exQuestions: 'Questions',
    exMarks: 'Marks',
    exTotal: '{n} questions in {m} minutes.',
    exTamilElig: 'Includes the qualifying Tamil eligibility test.',
    exSubjects: 'Subjects',
    exCourses: '{exam} courses',
    exNoCourses: 'New courses for this exam are on the way.',
    exFree: 'Free',
    exMocks: '{exam} mock tests with Vidya AI',
    exMocksText: 'Full mock tests in the exam pattern, timed and marked, with a review of where you lose marks. Free with every paid course.',
    exFaq: 'Questions',
    hdrDiscover: 'Discover new',
    hdrMenu: 'Menu',
  },
  // Hand-written Tamil (home page AI strings); everything else in Tamil is
  // machine-translated (messages.generated.js).
  ta: {
    heroTitle: 'ஆசிரியர் கனவை',
    heroTitleHighlight: 'நனவாக்குவோம்.',
    heroSubtitle: 'PG TRB, TN TET, UGC NET உள்ளிட்ட தமிழ்நாட்டின் அனைத்து ஆசிரியர் தேர்வுகளுக்கும் தயாராகுங்கள்: தமிழ் மற்றும் ஆங்கில வழிப் பாடங்கள், தரவரிசையுடன் நேர மாதிரித் தேர்வுகள், உங்கள் AI ஆசிரியர் வித்யா.',
    heroPointTutor: 'வித்யா AI ஆசிரியர், தமிழிலும் ஆங்கிலத்திலும்',
    heroPointPath: 'தரவரிசையுடன் நேர மாதிரித் தேர்வுகள்',
    heroWatchVideo: 'வீடியோவைப் பாருங்கள்',
    heroVideoTitle: 'ஒரு நிமிடத்தில் ThervuMitra',
    heroCloseVideo: 'வீடியோவை மூடு',
    aiTitle: 'AI உடன் கற்றுக்கொள்ளுங்கள்',
    aiSubtitle: 'உங்கள் தேர்வுத் தயாரிப்பின் ஒவ்வொரு படியிலும் அறிவார்ந்த உதவி, தமிழிலோ ஆங்கிலத்திலோ.',
    aiTutorTitle: 'ஒவ்வொரு பாடத்திட்டத்திலும் AI ஆசிரியர்',
    aiTutorText: 'எப்போது வேண்டுமானாலும் கேள்வி கேளுங்கள். உங்கள் பாடத்திட்டத்தின் பாடங்களைக் கொண்டே கருத்துகளை எளிய சொற்களில் விளக்கும், ஒரு தலைப்பு எங்கே உள்ளது என்று காட்டும், உங்கள் முன்னேற்றத்தையும் சரிபார்க்கும்.',
    aiPathTitle: 'உங்களுக்கென்றே ஒரு கற்றல் பாதை',
    aiPathText: 'ஒரு சிறிய கண்டறிதல் தேர்வு நீங்கள் ஏற்கனவே அறிந்தவற்றைக் கண்டறியும். பிறகு, நீங்கள் இன்னும் தேர்ச்சி பெற வேண்டிய கருத்துகளில் உங்கள் பாதை கவனம் செலுத்தும்.',
    aiLanguageTitle: 'தமிழில் பாடங்கள்',
    aiLanguageText: 'ஆங்கிலப் பாடங்களும் வீடியோ வசனங்களும் AI மூலம் தமிழில் மொழிபெயர்க்கப்படுகின்றன; ஒரே சொடுக்கில் மூலத்தைப் பார்க்கலாம்.',
    aiAssistantTitle: 'தேவைப்படும்போதெல்லாம் உதவி',
    aiAssistantText: 'பாடத்திட்டங்கள், சான்றிதழ்கள், ThervuMitra-ஐப் பயன்படுத்துவது பற்றிய உங்கள் கேள்விகளுக்கு எங்கள் AI உதவியாளர் ஒவ்வொரு பக்கத்திலும் பதிலளிக்கும்.',
    aiTeachTitle: 'ஆசிரியர்களுக்கான AI',
    aiTeachText: 'AI பாடத்திட்ட உருவாக்கியைக் கொண்டு பாடத்திட்ட வரைவை உருவாக்குங்கள்; உங்கள் கற்பவர்களின் முன்னேற்றம் பற்றி எளிய மொழியில் கேளுங்கள்.',
    aiNote: 'AI பதில்கள் தவறாக இருக்கலாம். முக்கியமான எதற்கும் பாடத்திட்டப் பொருளைச் சரிபாருங்கள்.',
    dashTabsLabel: 'டாஷ்போர்டு பகுதிகள்',
    dashTabCourses: 'எனது பாடத்திட்டங்கள்',
    dashTabGamification: 'விளையாட்டாக்கம்',
    dashTabLive: 'நேரலை வகுப்புகள்',
    lvDashEmpty: 'வரவிருக்கும் நேரலை வகுப்புகள் எதுவும் இல்லை. உங்கள் பாடத்திட்டத்தில் ஒன்று திட்டமிடப்பட்டதும், அது இங்கே தோன்றும்.',
    tagline: 'TRB தேர்வுக்கு உங்கள் நண்பன் · Your partner for every TRB exam.',
    footerLine: 'TRB தேர்வுக்கு உங்கள் நண்பன் · Your partner for every TRB exam.',
    initiative: 'Gheer Technologies நிறுவனத்தின் முன்முயற்சி.',
    heroPointLanguages: 'தமிழ் மற்றும் ஆங்கில வழி',
    heroPointCertificate: 'உங்கள் தேர்வு நாளுக்கான படிப்புத் திட்டம்',
    heroMockTitle: 'TN TET தாள் II · மாதிரித் தேர்வு',
    heroMockQuestion: 'கேள்வி 46 / 150',
    heroVidyaName: 'வித்யா',
    heroVidyaAnswer: 'பியாஜே: அறிவு வளர்ச்சியின் நான்கு நிலைகள்',
    heroRankTitle: 'தரவரிசை 12 / 4,500',
    heroRankText: 'TN TET மாதிரித் தேர்வு',
    statExams: 'ஆசிரியர் தேர்வுகள்',
    statLanguagesTaEn: 'தமிழ் மற்றும் ஆங்கிலம்',
    catalogSubtitle: 'தமிழ்நாட்டின் ஒவ்வொரு ஆசிரியர் நியமனத் தேர்வுக்கும் தமிழ் மற்றும் ஆங்கில வழிப் பாடங்கள்.',
    crHeroTitle: 'நீங்கள் எந்தத் தேர்வுக்குத் தயாராகிறீர்கள்?',
    crHeroText: 'PG TRB, TN TET, UGC NET மற்றும் பல தேர்வுகளுக்கான பாடங்கள், மாதிரித் தேர்வுகள், AI ஆசிரியர்: தமிழிலும் ஆங்கிலத்திலும்.',
    benefitLanguageTitle: 'தமிழ் மற்றும் ஆங்கிலம்',
    benefitLanguageText: 'தமிழிலோ ஆங்கிலத்திலோ படியுங்கள்: தளத்தின் மொழியை எப்போது வேண்டுமானாலும் மாற்றலாம்; பாடப் பகுதிகளை இயந்திர மொழிபெயர்ப்பில் தமிழில் படிக்கலாம்.',
    vdTagline: 'TRB தேர்வுகளுக்கான உங்கள் AI ஆசிரியர்',
    vdHeroTitle: 'தமிழ்நாட்டின் ஒவ்வொரு ஆசிரியர் தேர்வுக்கும் உங்கள் தனிப்பட்ட AI ஆசிரியர்.',
    vdHeroText: 'வித்யா உங்கள் பாடத்திட்டத்தின் ஒவ்வொரு அலகையும் விளக்கும், கணக்குகளைப் படிப்படியாகத் தீர்க்கும், பயிற்சிக் கேள்விகளையும் நேர மாதிரித் தேர்வுகளையும் தயாரிக்கும், உங்கள் விடைகளையும் பாடத் திட்டங்களையும் மதிப்பிடும், நீங்கள் எந்தத் தேர்வுக்குத் தயாராகிறீர்கள் என்பதை நினைவில் வைக்கும். தமிழிலும் ஆங்கிலத்திலும்.',
    vdStat1: 'தமிழிலும் ஆங்கிலத்திலும் பதில்கள்',
    vdFinalText: 'PG TRB, TN TET, UGC NET உள்ளிட்ட ஒவ்வொரு TRB தேர்வுக்கும் உங்கள் AI ஆசிரியர், தமிழிலோ ஆங்கிலத்திலோ, எந்த நேரத்திலும்.',
    vdFaqQ1: 'வித்யா எந்தத் தேர்வுகளையும் பாடங்களையும் உள்ளடக்கும்?',
    vdFaqA1: 'ThervuMitra தயார்படுத்தும் பத்துத் தேர்வுகள்: PG TRB, UG TRB, TN TET, SGT, TRB PET & Special Education, BEO, Polytechnic TRB, UGC NET, TN SET, CSIR NET, அவற்றின் அனைத்துப் பாடங்களும்.',
    vdFaqQ2: 'தமிழில் வேலை செய்யுமா?',
    vdFaqA2: 'ஆம். தமிழிலோ ஆங்கிலத்திலோ கேளுங்கள்; வித்யா அதே மொழியில் பதிலளிக்கும். உங்கள் சுயவிவரத்தில் பதில் மொழியையும் தேர்வு செய்யலாம். கலைச்சொற்களுக்கு ஆங்கிலப் பெயரும் தரப்படும்.',
    facetCategory: 'தேர்வு',
    facetLevel: 'பயிற்று மொழி',
    facetTopic: 'பாடம்',
    medium_tamil: 'தமிழ் வழி',
    medium_english: 'ஆங்கில வழி',
    medium_bilingual: 'இருமொழி',
    crStatCategories: '{count} தேர்வுகள்',
    crCategoriesTitle: 'உங்கள் தேர்வைத் தேர்ந்தெடுங்கள்',
    crCategoriesText: 'ஒரு தேர்வைத் தேர்ந்தெடுத்து அதன் பாடங்களைப் பாருங்கள், அல்லது கீழே எல்லாப் பாடங்களையும் பாருங்கள்.',
    homeExamsTitle: 'உங்கள் தேர்வைத் தேர்ந்தெடுங்கள்',
    homeExamsText: 'தமிழ்நாட்டின் பத்து ஆசிரியர் தேர்வுகளுக்கான பாடங்கள், மாதிரித் தேர்வுகள், வித்யா.',
    vdExam: 'இலக்குத் தேர்வு',
    vdExamSubject: 'பாடம்',
    vdChooseSubject: 'உங்கள் பாடத்தைத் தேர்ந்தெடுங்கள்',
    vdExamInvalid: 'இந்தத் தேர்வுக்கு உங்கள் பாடத்தைத் தேர்ந்தெடுங்கள்.',
    vdGoalsPlaceholder: 'எ.கா. ஜனவரி 2027-இல் PG TRB தமிழில் தேர்ச்சி; இலக்கணம், சங்க இலக்கியம் வலுப்படுத்த வேண்டும்.',
    vdMockPreset_default: 'என் தேர்வின் முறை',
    mkAdmPreset_custom: 'தனிப்பயன்',
    vdMockPreset_tntet_p1: 'TN TET தாள் I (+1, எதிர்மறை இல்லை)',
    mkAdmPreset_tntet_p1: 'TN TET தாள் I',
    vdMockPreset_tntet_p2: 'TN TET தாள் II (+1, எதிர்மறை இல்லை)',
    mkAdmPreset_tntet_p2: 'TN TET தாள் II',
    vdMockPreset_pgtrb: 'PG TRB (+1, எதிர்மறை இல்லை)',
    mkAdmPreset_pgtrb: 'PG TRB',
    vdMockPreset_tamil_elig: 'தமிழ்த் தகுதித் தேர்வு (+1, எதிர்மறை இல்லை)',
    mkAdmPreset_tamil_elig: 'தமிழ்த் தகுதித் தேர்வு',
    vdMockPreset_ugtrb: 'UG TRB (பட்டதாரி ஆசிரியர்) (+1, எதிர்மறை இல்லை)',
    mkAdmPreset_ugtrb: 'UG TRB (பட்டதாரி ஆசிரியர்)',
    vdMockPreset_sgt: 'SGT (இடைநிலை ஆசிரியர்) (+1, எதிர்மறை இல்லை)',
    mkAdmPreset_sgt: 'SGT (இடைநிலை ஆசிரியர்)',
    vdMockPreset_beo: 'BEO (வட்டாரக் கல்வி அலுவலர்) (+1, எதிர்மறை இல்லை)',
    mkAdmPreset_beo: 'BEO (வட்டாரக் கல்வி அலுவலர்)',
    vdMockPreset_poly_trb: 'பாலிடெக்னிக் TRB (+1, எதிர்மறை இல்லை)',
    mkAdmPreset_poly_trb: 'பாலிடெக்னிக் TRB',
    vdMockPreset_pet_sped: 'TRB உடற்கல்வி & சிறப்புக் கல்வி (+1, எதிர்மறை இல்லை)',
    mkAdmPreset_pet_sped: 'TRB உடற்கல்வி & சிறப்புக் கல்வி',
    vdMockPreset_ugcnet: 'UGC NET (+2, எதிர்மறை இல்லை)',
    mkAdmPreset_ugcnet: 'UGC NET',
    vdMockPreset_tnset: 'TN SET (+2, எதிர்மறை இல்லை)',
    mkAdmPreset_tnset: 'TN SET',
    vdMockPreset_csirnet: 'CSIR NET (+2/+4, எதிர்மறை மதிப்பெண்)',
    mkAdmPreset_csirnet: 'CSIR NET',
    vdPreset_short5: 'கருத்து விளக்கம், 5 மதிப்பெண் (100 சொற்கள்)',
    vdPreset_long10: 'விரிவான விடை, 10 மதிப்பெண் (250 சொற்கள்)',
    vdPreset_lesson_plan: 'பாடத் திட்டம், 25 மதிப்பெண் (600 சொற்கள்)',
    vdPreset_other: 'பிற',
    vdPhase_Foundation: 'அடித்தளம்',
    vdPhase_Practice: 'பயிற்சி',
    vdPhase_Mock_sprint: 'மாதிரித் தேர்வு ஓட்டம்',
    vdPhase_Final_revision: 'இறுதித் திருப்புதல்',
    vdPlaceholder_ask: 'ஒரு கேள்வி கேளுங்கள் அல்லது எதைக் கற்க விரும்புகிறீர்கள் என்று சொல்லுங்கள்…',
    vdPlaceholder_solve: 'கணக்கைத் தட்டச்சு செய்யுங்கள் அல்லது ஒட்டுங்கள்…',
    vdPlaceholder_quiz: 'வினாடி வினாவுக்கான தலைப்பு, எ.கா. "குழந்தை வளர்ச்சி: பியாஜே"…',
    vdPlaceholder_research: 'வித்யா எதை ஆராய வேண்டும்?',
    vdPlaceholder_visualize: 'வித்யா எதை வரைய வேண்டும்? எ.கா. "சங்க இலக்கிய மன வரைபடம்"…',
    vdPlaceholder_evaluate: 'கேள்வியையும் உங்கள் விடை அல்லது பாடத் திட்டத்தையும் ஒட்டுங்கள் அல்லது புகைப்படம் எடுங்கள் (3 பக்கங்கள் வரை)',
    vdStarter_ask1: 'பியாஜேவின் அறிவு வளர்ச்சி நிலைகளை வகுப்பறை எடுத்துக்காட்டுகளுடன் விளக்குங்கள்',
    vdStarter_ask2: 'என் தேர்வு நாளுக்கான படிப்புத் திட்டம் தாருங்கள்',
    vdStarter_ask3: 'வளரறி மதிப்பீடுக்கும் தொகுத்தறி மதிப்பீடுக்கும் என்ன வேறுபாடு?',
    vdStarter_ask4: 'சங்க இலக்கியத்தின் ஐந்திணைகளை விளக்குங்கள்',
    vdStarter_solve1: '240 மீ நீளமுள்ள ரயில் ஒரு கம்பத்தை 12 வினாடிகளில் கடக்கிறது. அதன் வேகத்தை கி.மீ/மணியில் காண்க.',
    vdStarter_solve2: 'தீர்க்க: x² − 5x + 6 = 0, முறையையும் விளக்குக',
    vdStarter_solve3: '0.01 M HCl கரைசலின் pH மதிப்பைக் காண்க',
    vdStarter_solve4: 'சராசரி, இடைநிலை, முகடு காண்க: 4, 7, 7, 9, 13',
    vdStarter_quiz1: 'குழந்தை வளர்ச்சியும் கற்பித்தலும்',
    vdStarter_quiz2: 'தமிழ்த் தகுதித் தேர்வுக்கான தமிழ் இலக்கணம்',
    vdStarter_quiz3: 'UGC NET தாள் I: ஆய்வுத் திறன்',
    vdStarter_quiz4: 'என் பலவீனமான தலைப்புகளைத் திருப்புதல் செய்',
    vdStarter_research1: 'தமிழ்நாட்டின் சமீபத்திய பள்ளிக் கல்வித் திட்டங்கள்',
    vdStarter_research2: 'தேசியக் கல்விக் கொள்கை 2020 ஆசிரியர் கல்வி பற்றிக் கூறுவது',
    vdStarter_research3: 'இந்திய வகுப்பறைகளில் உள்ளடங்கிய கல்வி பற்றிய சமீபத்திய ஆய்வுகள்',
    vdStarter_research4: 'கல்வி உரிமைச் சட்டம்: ஆசிரியர்களுக்கான முக்கிய விதிகள்',
    vdStarter_visualize1: 'கற்றல் கோட்பாடுகளின் மன வரைபடம்',
    vdStarter_visualize2: 'ஒளிச்சேர்க்கையின் பாய்வுப் படம்',
    vdStarter_visualize3: 'சங்க காலம் முதல் இக்காலம் வரை தமிழ் இலக்கியக் காலவரிசை',
    vdStarter_visualize4: 'கோல்பர்க்கின் அறநெறி வளர்ச்சி நிலைகள் விளக்கப்படம்',
    vdStarter_evaluate1: 'கல்வி உளவியலில் பயிற்சிக்கு ஒரு 10 மதிப்பெண் கேள்வி தாருங்கள்',
    vdStarter_evaluate2: '6-ஆம் வகுப்பு அறிவியலுக்கான என் பாடத் திட்டத்தைச் சரிபாருங்கள் (ஒட்டுகிறேன்)',
    vdStarter_evaluate3: 'என் விடையை மதிப்பிடுங்கள் (ஒட்டுகிறேன்)',
    vdFeatText_evaluate: 'விடை அல்லது பாடத் திட்டத்தை எழுதுங்கள் அல்லது புகைப்படம் எடுங்கள்; மதிப்பெண், மதிப்பீட்டு அளவுகோல், திருத்த வேண்டியவை, மாதிரி விடை கிடைக்கும்.',
    vdModeHelp_evaluate: 'விடை அல்லது பாடத் திட்டத்தை ஒட்டுங்கள் அல்லது புகைப்படம் எடுங்கள்; மதிப்பெண், கருத்து, மாதிரி விடை பெறுங்கள்.',
    vdModeHelp_solve: 'கணிதம், இயற்பியல், வேதியியல் அல்லது புள்ளியியல் கேள்வியை ஒட்டுங்கள்; முழுமையான தீர்வு கிடைக்கும்.',
    vdMockTopicPh: 'எ.கா. கல்வி உளவியல்: கற்றல் கோட்பாடுகள்',
    mkAdmF_examPh: 'எ.கா. TN TET தாள் II',
    mkAdmSectionMarks: 'பகுதி வாரியான மதிப்பெண்: {marks}',
    lhProgress: '{pct}% முடிந்தது',
    lhCourseProgress: 'பாட முன்னேற்றம்',
    lhStart: 'பாடத்தைத் தொடங்குங்கள்',
    lhResume: 'பாடத்தைத் தொடருங்கள்',
    lhStartBlurb: 'இன்றே உங்கள் பாடத்தைத் தொடங்குங்கள்',
    lhResumeBlurb: 'விட்ட இடத்திலிருந்து தொடருங்கள்',
    lhCourses: 'படிப்புகள்',
    lhHeaderNav: 'முதன்மை மெனு',
    lhCourseNav: 'பாடநெறி மெனு',
    vdHowToGet: 'Vidya AI-ஐ எப்படிப் பெறுவது',
    vdAccessEyebrow: 'அணுகல்',
    vdAccessTitle: 'Vidya AI-ஐப் பெற இரண்டு வழிகள்',
    vdAccessBest: 'பரிந்துரைக்கப்படுகிறது',
    vdAccessCourseTitle: 'எந்தக் கட்டணப் பாடநெறியுடனும் இலவசம்',
    vdAccessCourseText: 'ThervuMitra-வின் எந்தக் கட்டணப் பாடநெறியை வாங்கினாலும், அந்தப் பாடநெறி உங்களிடம் இருக்கும் வரை Vidya AI உடன் சேர்ந்தே வரும். தனியாக வாங்க வேண்டியதில்லை.',
    vdBrowseCourses: 'பாடநெறிகளைப் பாருங்கள்',
    vdAccessPlanTitle: 'பாடநெறி இல்லையா? Vidya AI திட்டத்தைப் பெறுங்கள்',
    vdAccessPlanText: 'நீங்கள் எந்தக் கட்டணப் பாடநெறியையும் வாங்கவில்லை என்றால், Vidya AI திட்டத்தைத் தனியாக வாங்கலாம்.',
    vdAccessPlanNotNeeded: 'தேவையில்லை: உங்கள் பாடநெறியில் Vidya AI ஏற்கெனவே உள்ளது.',
    vdIncludedWithCourse: 'உங்கள் பாடநெறியுடன் சேர்ந்துள்ளது',
    vdIncludedNotice: 'Vidya AI உங்கள் பாடநெறியுடன் ஏற்கெனவே உள்ளது. திட்டம் தேவையில்லை.',
    vdPlansCourseNote: 'எந்தக் கட்டணப் பாடநெறியை வாங்கினாலும் Vidya AI-யும் கிடைக்கும்.',
    vdFaqA3: 'பாடநெறியுடன் வந்தாலும் திட்டத்துடன் வந்தாலும், Vidya ஒரு நாளைக்குச் சுமார் 150 செய்திகளை வழங்குகிறது; ஆய்வும் நீண்ட வினாடி வினாக்களும் சற்று அதிகமாகக் கணக்கிடப்படும். இந்த அளவு ஒவ்வொரு நாளும் புதுப்பிக்கப்படும்.',
    vdFaqQ4: 'எனது Vidya AI திட்டம் தானாகப் புதுப்பிக்கப்படுமா?',
    vdFaqA4: 'இல்லை. ஒரு திட்டம் அதன் காலம் வரை செயல்பட்டு முடிவடையும். எப்போது வேண்டுமானாலும் மற்றொரு திட்டத்தை வாங்கலாம்; தற்போதையது முடியும்போது அது தொடங்கும். நீங்கள் ஒரு கட்டணப் பாடநெறியை வாங்கினால், Vidya AI அதனுடன் வரும்; திட்டம் தேவையில்லை.',
    vdFaqQ6: 'நான் ஒரு பாடநெறியை வாங்கினேன். Vidya AI திட்டம் தேவையா?',
    vdFaqA6: 'இல்லை. ஒவ்வொரு கட்டணப் பாடநெறியிலும், அந்தப் பாடநெறி உங்களிடம் இருக்கும் வரை Vidya AI சேர்ந்துள்ளது. Vidya AI-ஐத் திறந்தால் போதும்.',
    vdFaqQ7: 'நான் எந்தப் பாடநெறியையும் வாங்கவில்லை. Vidya AI-ஐப் பயன்படுத்த முடியுமா?',
    vdFaqA7: 'ஆம். Vidya AI திட்டத்தைத் தனியாக வாங்கலாம், அல்லது எந்தக் கட்டணப் பாடநெறியையும் வாங்கி Vidya AI-ஐச் சேர்த்துப் பெறலாம்.',
    shIncludedTitle: 'இந்தப் பாடநெறியுடன் சேர்ந்தவை',
    shIncludedOwned: 'உங்கள் பாடநெறியுடன் சேர்ந்தவை',
    shIncTutor: 'AI ஆசிரியர்',
    shIncTutorText: 'இந்தப் பாடநெறியின் பாடங்களைப் பற்றி எப்போது வேண்டுமானாலும் கேள்வி கேட்டு, படிப்படியான உதவியைப் பெறுங்கள்.',
    shIncVidya: 'Vidya AI',
    shIncVidyaText: 'உங்கள் தேர்வுத் தயாரிப்பு உதவியாளர்: மாதிரித் தேர்வுகள், படிப்புத் திட்டம், விடை மதிப்பீடு — உங்கள் அனைத்துத் தேர்வுகளுக்கும், இந்தப் பாடநெறி உங்களிடம் இருக்கும் வரை. தனித் திட்டம் தேவையில்லை.',
    shIncVidyaLink: 'Vidya AI பற்றி',
    shSkippedIncluded: 'கட்டணம் இல்லை: உங்கள் கூடையில் உள்ள பாடநெறியுடன் Vidya AI இலவசம்',
    vdGetPass: 'இந்தத் திட்டத்தைப் பெறுங்கள்',
    vdExtendPass: 'திட்டத்தை நீட்டியுங்கள்',
    vdPassUntil: '{date} வரை திட்டம் செயலில் உள்ளது',
    vdPlansTitle: 'Vidya AI திட்டங்கள்',
    vdExtendTitle: 'உங்கள் Vidya AI திட்டத்தை நீட்டியுங்கள்',
    vdCouldNotAdd: 'திட்டத்தை உங்கள் கூடையில் சேர்க்க முடியவில்லை. மீண்டும் முயலுங்கள்.',
    vdClose: 'மூடு',
    vdToday: 'இன்று',
    vdChats: 'அரட்டைகள்',
    vdNotebook: 'குறிப்பேடு',
    vdMistakeBank: 'தவறுகள் தொகுப்பு',
    vdMyAnswers: 'என் விடைகள்',
    vdMockTests: 'மாதிரித் தேர்வுகள்',
    vdMyProfile: 'என் சுயவிவரம்',
    vdActivity: 'செயல்பாடு',
    vdNewChat: 'புதிய அரட்டை',
    vdSearchChats: 'அரட்டைகளைத் தேடுங்கள்',
    vdNoChats: 'இன்னும் அரட்டைகள் இல்லை.',
    vdStat2: 'படிப்படியான தீர்வுகள்',
    vdStat3: 'ஒவ்வொரு தகவலுக்கும் ஆதாரம்',
    vdHowEyebrow: 'மூன்று படிகள்',
    vdPlansEyebrow: 'திட்டங்கள்',
    vdForDays: '{n} நாட்களுக்கு',
    vdTrust: 'வரி விலைப்பட்டியலுடன், GST உட்பட. பாதுகாப்பான கட்டணம். தானாகப் புதுப்பிக்கப்படாது.',
    vdFinalTitle: 'இன்றே Vidya உடன் படிக்கத் தொடங்குங்கள்',
    vdGreeting_morning: 'காலை வணக்கம்',
    vdGreeting_afternoon: 'மதிய வணக்கம்',
    vdGreeting_evening: 'மாலை வணக்கம்',
    vdCountdown: '{exam} · {n} நாட்கள்',
    vdStreak: '🔥 {n} நாட்கள்',
    vdStreakOne: '🔥 1 நாள்',
    vdStreakRisk: 'தொடர்ந்து {n} நாள் படித்ததைத் தக்கவைக்க இன்றும் படியுங்கள்',
    vdTodayEmpty: 'இந்த நாளுக்கு எதுவும் திட்டமிடப்படவில்லை.',
    vdTodayProgress: 'இன்றைய முன்னேற்றம்',
    vdTodayProgressText: '{done}/{total} பணிகள் · {minutes}/{planned} நிமி',
    vdTaskStart: 'தொடங்கு',
    vdTaskSkip: 'தவிர்',
    vdTaskMarkDone: 'முடிந்ததாகக் குறி',
    vdTaskKind_quiz: 'வினாடி வினா',
    vdTaskKind_mock: 'மாதிரித் தேர்வு',
    vdTaskKind_evaluate: 'விடை எழுதுதல்',
    vdTaskKind_study: 'படிப்பு',
    vdTaskKind_review: 'தவறுகளைத் திருப்புதல்',
    vdMinutes: '{n} நிமி',
    vdThisWeek: 'இந்த வாரம்',
    vdPlanTitle: 'திட்டம்',
    vdDailyTime: 'தினசரி படிப்பு நேரம்',
    vdReplan: 'என் வாரத்தை மீண்டும் திட்டமிடு',
    vdPlanning: 'உங்கள் வாரம் திட்டமிடப்படுகிறது…',
    vdPlanErr_limit: 'இன்று பலமுறை மீண்டும் திட்டமிட்டுவிட்டீர்கள். நாளை மீண்டும் முயலுங்கள்.',
    vdPlanErr_unavailable: 'இப்போது Vidya-வால் உங்கள் வாரத்தைத் திட்டமிட முடியவில்லை. மீண்டும் முயலுங்கள்.',
    vdPlanErr_profile: 'உங்கள் சுயவிவரத்தை ஏற்ற முடியவில்லை, எனவே எதுவும் மாற்றப்படவில்லை. மீண்டும் முயலுங்கள்.',
    vdPlanLoadErr: 'இப்போது உங்கள் திட்டத்தை ஏற்ற முடியவில்லை.',
    vdRetry: 'மீண்டும் முயலுங்கள்',
    vdSetupHelp: 'உங்கள் தேர்வையும், தினமும் எவ்வளவு நேரம் படிக்க முடியும் என்பதையும் Vidya-விடம் சொல்லுங்கள். அடுத்த 7 நாட்களுக்கான திட்டத்தை Vidya தயாரிக்கும்.',
    vdMakePlan: 'என் திட்டத்தைத் தயாரி',
    vdAttachPhoto: 'புகைப்படம் சேர்',
    vdPhotoMax: '3 புகைப்படங்கள் வரை சேர்க்கலாம்.',
    vdPhotoFailed: 'ஒரு புகைப்படம் பதிவேறவில்லை. மீண்டும் முயல அதைத் தொடுங்கள், அல்லது நீக்குங்கள்.',
    vdPhotoDecode: 'அந்தப் புகைப்படத்தைப் படிக்க முடியவில்லை.',
    vdPhotoTooLarge: 'அந்தப் புகைப்படம் மிகப் பெரியது.',
    vdPhotoDaily: 'இன்றைய புகைப்பட வரம்பை அடைந்துவிட்டீர்கள்.',
    vdPhotoRemove: 'புகைப்படத்தை நீக்கு',
    vdPhotoOpen: 'புகைப்படத்தைத் திற',
    vdStarterSnap: 'ஒரு கேள்வியைப் புகைப்படம் எடுங்கள்',
    vdTool_show_evaluation: 'உங்கள் விடை மதிப்பிடப்படுகிறது',
    vdFeat_evaluate: 'விடை மதிப்பீடு',
    vdMode_evaluate: 'மதிப்பிடு',
    vdModeTitle_evaluate: 'உங்கள் விடைக்கு மதிப்பெண் பெறுங்கள்',
    vdStarterEvalPhoto: 'கையால் எழுதிய என் விடையை மதிப்பிடுங்கள்',
    vdPreset: 'தாள்',
    vdMaxMarks: 'அதிகபட்ச மதிப்பெண்',
    vdWordLimit: 'சொல் வரம்பு',
    vdBand_NeedsWork: 'மேம்பட வேண்டும்',
    vdBand_Average: 'சராசரி',
    vdBand_Good: 'நன்று',
    vdBand_Excellent: 'மிக நன்று',
    vdEvalEstimate: 'AI மதிப்பீடு. உண்மையான தேர்வாளர்களின் மதிப்பீடு மாறுபடலாம்.',
    vdEvalNotSaved: 'சேமிக்கப்படவில்லை',
    vdEvalWords: 'சொற்கள்',
    vdEvalOverLimit: 'வரம்பைத் தாண்டியது',
    vdEvalStrengths: 'பலங்கள்',
    vdEvalImprove: 'மேம்படுத்துவது எப்படி',
    vdEvalMissing: 'விடுபட்ட கருத்துகள்',
    vdEvalModel: 'மாதிரி விடையைக் காட்டு',
    vdEvalModelHide: 'மாதிரி விடையை மறை',
    vdEvalTryAgain: 'இந்தக் கேள்வியை மீண்டும் முயலுங்கள்',
    vdAnswersEmpty: 'இன்னும் மதிப்பிடப்பட்ட விடைகள் இல்லை.',
    vdAnswersStart: 'ஒரு விடையை மதிப்பிடுங்கள்',
    vdAnswersDelete: 'இந்த மதிப்பீட்டை நீக்கவா?',
    vdAnswersDeleted: 'உரையாடல் நீக்கப்பட்டது',
    vdAnswersAll: 'அனைத்துத் தாள்களும்',
    vdAnswersCount: '{n} விடைகள்',
    vdAnswersAvg: 'சராசரி {pct}%',
    vdAnswersRecent: 'சமீபத்திய மதிப்பெண்கள்',
    vdMockIntro: 'நேரக் கட்டுப்பாடும் எதிர்மறை மதிப்பெண்ணும் கொண்ட தேர்வு முறைத் தேர்வுகள்; பிறகு எங்கே மதிப்பெண் இழக்கிறீர்கள் என்பதன் முழுப் பகுப்பாய்வு.',
    vdMockShared: 'பொது',
    vdMockMine: 'என்னுடையவை',
    vdMockNoShared: 'இன்னும் தேர்வுத் தாள்கள் இல்லை. அதுவரை நீங்களே ஒரு மாதிரித் தேர்வை உருவாக்குங்கள்.',
    vdMockNoMine: 'இன்னும் மாதிரித் தேர்வுகள் இல்லை. எந்தத் தலைப்பிலும் ஒன்றை உருவாக்குங்கள்.',
    vdMockLoadFailed: 'மாதிரித் தேர்வுகளை ஏற்ற முடியவில்லை.',
    vdMockRetry: 'மீண்டும் முயலுங்கள்',
    vdMockQuestionsN: '{n} கேள்விகள்',
    vdMockMinutes: '{n} நிமி',
    vdMockTakers: '{n} பேர் எழுதியுள்ளனர்',
    vdMockBest: 'சிறந்தது: {score} / {max}',
    vdMockStart: 'தொடங்கு',
    vdMockResume: 'தொடர்',
    vdMockRetake: 'மீண்டும் எழுது (பயிற்சி)',
    vdMockAiMade: 'AI உருவாக்கியது, சரிபார்க்கப்படவில்லை',
    vdMockMaking: 'உங்கள் மாதிரித் தேர்வு தயாராகிறது… {done}/{total}',
    vdMockGenError: 'Vidya-வால் இந்த மாதிரித் தேர்வை முடிக்க முடியவில்லை: {total} கேள்விகளில் {done} தயார்.',
    vdMockRetryGen: 'மீண்டும் முயலுங்கள்',
    vdMockWaitMaking: 'இது தயாரானதும் இன்னொரு மாதிரித் தேர்வை உருவாக்கலாம்.',
    vdMockMake: 'மாதிரித் தேர்வு உருவாக்கு',
    vdMockTopic: 'தலைப்பு',
    vdMockCount: 'கேள்விகள் (10–50)',
    vdMockPreset: 'மதிப்பெண் முறை',
    vdMockMakeHelp: 'ஒரு கேள்விக்கு ஒரு நிமிடம். ஒரு நாளில் 3 மாதிரித் தேர்வுகள் வரை உருவாக்கலாம்.',
    vdMockCreate: 'என் மாதிரித் தேர்வை உருவாக்கு',
    vdMockCreating: 'தொடங்குகிறது…',
    vdMockCancel: 'ரத்து செய்',
    vdMockForTask: 'உங்கள் திட்டத்துக்காக: {topic}',
    vdMockSuggested: 'இந்தத் தேர்வுத் தாள் உங்கள் தேர்வுக்குப் பொருந்துகிறது. இது உங்கள் திட்டத்தில் கணக்கில் கொள்ளப்படும்.',
    vdMockForTaskMake: 'கீழே இந்தத் தலைப்பில் ஒரு மாதிரித் தேர்வை உருவாக்குங்கள். இது உங்கள் திட்டத்தில் கணக்கில் கொள்ளப்படும்.',
    vdMockMakeInstead: 'அதற்குப் பதிலாக {topic} பற்றி ஒரு மாதிரித் தேர்வை உருவாக்கு',
    vdMockErr_limit: 'ஒரு நாளில் 3 மாதிரித் தேர்வுகள் உருவாக்கலாம். நாளை மீண்டும் முயலுங்கள்.',
    vdMockErr_busy: 'Vidya இன்னும் உங்கள் மற்றொரு மாதிரித் தேர்வைத் தயாரிக்கிறது. அது முடியும் வரை காத்திருங்கள்.',
    vdMockErr_closed: 'அந்தத் தேர்வுக்கான நேரம் முடிந்ததால், அது சமர்ப்பிக்கப்பட்டது.',
    vdMockErr_empty: 'இந்த மாதிரித் தேர்வில் இன்னும் கேள்விகள் இல்லை.',
    vdMockErr_generic: 'ஏதோ தவறு நடந்தது. மீண்டும் முயலுங்கள்.',
    vdMockLoading: 'உங்கள் தேர்வு திறக்கப்படுகிறது…',
    vdMockTimeLeft: 'மீதமுள்ள நேரம்',
    vdMockFiveLeft: 'இன்னும் 5 நிமிடங்களே உள்ளன.',
    vdMockSave_saved: 'சேமிக்கப்பட்டது',
    vdMockSave_saving: 'சேமிக்கப்படுகிறது…',
    vdMockSave_failed: 'இன்னும் சேமிக்கப்படவில்லை, மீண்டும் முயல்கிறது',
    vdMockSubmit: 'சமர்ப்பி',
    vdMockLeave: 'வெளியேறு',
    vdMockLeaveConfirm: 'தேர்விலிருந்து வெளியேறவா? நேரம் ஓடிக்கொண்டே இருக்கும்; நேரம் முடியும் வரை மீண்டும் தொடரலாம்.',
    vdMockSections: 'பிரிவுகள்',
    vdMockSectionN: 'பிரிவு {n}',
    vdMockQuestionN: 'கேள்வி {n} / {total}',
    vdMockOptions: 'விருப்பங்கள்',
    vdMockMark: 'மறுபார்வைக்குக் குறி',
    vdMockClear: 'அழி',
    vdMockPrevious: 'முந்தையது',
    vdMockSaveNext: 'சேமித்து அடுத்தது',
    vdMockKeys: 'விசைகள்: தேர்ந்தெடுக்க 1–4 அல்லது A–D, N அடுத்தது, P முந்தையது, M குறி',
    vdMockPalette: 'கேள்விகள்',
    vdMockClose: 'மூடு',
    vdMockAnsweredOf: '{n}/{total} விடையளிக்கப்பட்டது',
    vdMockPaletteItem: 'கேள்வி {n}: {state}',
    vdMockState_notVisited: 'பார்க்கப்படவில்லை',
    vdMockState_notAnswered: 'விடையளிக்கப்படவில்லை',
    vdMockState_answered: 'விடையளிக்கப்பட்டது',
    vdMockState_marked: 'மறுபார்வைக்குக் குறிக்கப்பட்டது',
    vdMockState_answeredMarked: 'விடையளித்துக் குறிக்கப்பட்டது',
    vdMockSubmitTitle: 'தேர்வைச் சமர்ப்பிக்கவா?',
    vdMockUnanswered: '{n} கேள்விகளுக்கு விடையளிக்கவில்லை.',
    vdMockUnansweredOne: '1 கேள்விக்கு விடையளிக்கவில்லை.',
    vdMockAllAnswered: 'எல்லாக் கேள்விகளுக்கும் விடையளித்துவிட்டீர்கள்.',
    vdMockKeepGoing: 'தொடர்ந்து எழுது',
    vdMockSubmitNow: 'இப்போதே சமர்ப்பி',
    vdMockSubmitting: 'சமர்ப்பிக்கப்படுகிறது…',
    vdMockSubmitFailed: 'உங்கள் தேர்வைச் சமர்ப்பிக்க முடியவில்லை. இணைய இணைப்பைச் சரிபாருங்கள்.',
    vdMockResults: 'முடிவுகள்',
    vdMockPractice: 'பயிற்சி முயற்சி',
    vdMockFewTakers: 'இன்னும் போதுமான பேர் எழுதவில்லை',
    vdMockBetter: 'எழுதிய {n} பேரில் {pct}% பேரை விடச் சிறப்பு',
    vdMockCorrect: 'சரி',
    vdMockWrong: 'தவறு',
    vdMockSkipped: 'தவிர்த்தவை',
    vdMockAccuracy: 'துல்லியம்',
    vdMockColSection: 'பிரிவு',
    vdMockColAttempted: 'முயன்றவை',
    vdMockColScore: 'மதிப்பெண்',
    vdMockColAvg: 'சராசரி நேரம்',
    vdMockSlowest: 'அதிக நேரம் எடுத்த கேள்விகள்',
    vdMockWrongTime: 'தவறான விடைகளுக்குச் செலவிட்ட நேரம்: {time}',
    vdMockSolutions: 'தீர்வுகள்',
    vdMockFilter_all: 'அனைத்தும்',
    vdMockFilter_wrong: 'தவறு',
    vdMockFilter_skipped: 'தவிர்த்தவை',
    vdMockFilter_marked: 'குறித்தவை',
    vdMockNone: 'இங்கே கேள்விகள் இல்லை.',
    vdMockQ: 'கே.{n}',
    vdMockOutcome_right: 'சரி',
    vdMockOutcome_wrong: 'தவறு',
    vdMockOutcome_skipped: 'தவிர்க்கப்பட்டது',
    vdMockMarkedTag: 'குறிக்கப்பட்டது',
    vdMockTime: 'நேரம்: {time}',
    vdMockYourAnswer: 'உங்கள் விடை',
    vdMockCorrectAnswer: 'சரியான விடை',
    vdMockNotAnswered: 'இந்தக் கேள்விக்கு நீங்கள் விடையளிக்கவில்லை.',
    vdMockAskVidya: 'Vidya-விடம் கேளுங்கள்',
    vdMockAskPrompt: 'இந்தக் கேள்வியை விளக்குங்கள்: {stem} விருப்பங்கள்: {options}. நான் {choice} தேர்ந்தெடுத்தேன்; சரியான விடை {answer}.',
    vdMockAskSkipped: 'இந்தக் கேள்வியை விளக்குங்கள்: {stem} விருப்பங்கள்: {options}. நான் விடையளிக்கவில்லை; சரியான விடை {answer}.',
    vdMockBackToList: 'மாதிரித் தேர்வுகளுக்குத் திரும்பு',
    vdMockBackToPlan: '"இன்று" பகுதிக்குத் திரும்பு',
    vdMockCheckAnswer: 'விடையைச் சரிபார்',
    vdBuiltFor: 'இவற்றுக்காக உருவாக்கப்பட்டது',
    vdFeat_ask: 'எதையும் கேளுங்கள்',
    vdFeatText_ask: 'அடிப்படை முதல் உயர்நிலை வரை, உங்கள் நிலைக்கு ஏற்ற தெளிவான விளக்கங்கள்; எடுத்துக்காட்டுகளும் புரிதலைச் சோதிக்கும் சிறு கேள்விகளும் உண்டு.',
    vdFeat_solve: 'படிப்படியான தீர்வு',
    vdFeatText_solve: 'ஒவ்வொரு படியுடனும் முழுத் தீர்வு: சூத்திரம், அது ஏன் பொருந்தும், விடைச் சரிபார்ப்பு, பொதுவாக மாணவர்கள் செய்யும் தவறு.',
    vdFeat_quiz: 'பயிற்சி வினாடி வினாக்கள்',
    vdFeatText_quiz: 'எந்தத் தலைப்பிலும், எந்தக் கடினநிலையிலும் தேர்வு முறைக் கேள்விகள்; விளக்கத்துடன் உடனே மதிப்பிடப்படும்.',
    vdFeatText_visualize: 'கடினமான தலைப்புகளை எளிதாக நினைவில் வைக்க உதவும் மன வரைபடங்கள், பாய்வுப் படங்கள், காலவரிசைகள், விளக்கப்படங்கள்.',
    vdFeatText_notebook: 'விடைகள், தீர்வுகள், வரைபடங்களைச் சேமித்துப் பின்னர் திருப்புதல் செய்யுங்கள்.',
    vdFeat_mistakes: 'தவறுகள் தொகுப்பு',
    vdFeatText_mistakes: 'ஒவ்வொரு தவறான விடையும் சேமிக்கப்பட்டு, நீங்கள் நன்கு கற்கும் வரை திருப்புதலுக்குத் திரும்ப வரும்.',
    vdFeat_profile: 'உங்களை நினைவில் வைக்கும்',
    vdFeatText_profile: 'உங்கள் தேர்வு, இலக்குகள், பலவீனமான தலைப்புகள் ஒவ்வொரு விடையையும் வடிவமைக்கின்றன. Vidya நினைவில் வைத்திருப்பதைப் பார்க்கவும் மாற்றவும் முடியும்.',
    vdHowTitle: 'இது எப்படிச் செயல்படுகிறது',
    vdHow1: 'உங்கள் இலக்கை Vidya-விடம் சொல்லுங்கள்',
    vdHowText1: 'உங்கள் தேர்வு, தேதி, நிலை: ஒருமுறை சொன்னால் போதும். அவற்றுக்கு ஏற்ப Vidya திட்டமிடும்.',
    vdHow2: 'உங்கள் வழியில் கற்றுக்கொள்ளுங்கள்',
    vdHowText2: 'கேளுங்கள், தீர்வு காணுங்கள், வினாடி வினா எழுதுங்கள், ஆராயுங்கள் அல்லது படமாகப் பாருங்கள்; Vidya எடுக்கும் ஒவ்வொரு படியையும் காணலாம்.',
    vdHow3: 'உங்கள் குறைகளைச் சரிசெய்யுங்கள்',
    vdHowText3: 'தவறுகள் திருப்புதலுக்குத் திரும்ப வரும்; பலவீனமான தலைப்புகளுக்குக் கூடுதல் பயிற்சி கிடைக்கும்.',
    vdPlansText: 'ஒருமுறை கட்டணம், தானாகப் புதுப்பிக்கப்படாது. தற்போதைய முடிவுத் தேதியிலிருந்து நீட்டிக்க எப்போது வேண்டுமானாலும் மீண்டும் வாங்கலாம்.',
    vdPerMonthEquiv: 'மாதம் வெறும் ₹{price}',
    vdSave_pct: '{pct}% சேமிப்பு',
    vdPlanGst: 'விலையில் GST அடங்கும், வரி விலைப்பட்டியலுடன்',
    vdNoPlans: 'Vidya திட்டங்கள் இப்போது விற்பனையில் இல்லை. விரைவில் மீண்டும் பாருங்கள்.',
    vdFaqA5: 'Vidya மிகவும் திறமையானது, ஆனால் தவறு செய்யக்கூடும். மாறக்கூடிய தகவல்களுக்கு இணையத்தில் தேடி ஆதாரங்களைக் காட்டும். முக்கியமானவற்றை உங்கள் பாடநூல்கள் அல்லது அதிகாரப்பூர்வ ஆதாரங்களுடன் சரிபாருங்கள்.',
    vdPin: 'அரட்டையைப் பின் செய்',
    vdUnpin: 'பின்னை நீக்கு',
    vdDueN: '{n} திருப்புதலுக்கு உள்ளன',
    vdModeTitle_ask: 'Vidya-விடம் எதையும் கேளுங்கள்',
    vdModeTitle_solve: 'ஒரு கணக்கைப் படிப்படியாகத் தீர்க்கவும்',
    vdModeTitle_visualize: 'படமாகப் பாருங்கள்',
    vdModeHelp_ask: 'கருத்துகள், ஐயங்கள், சுருக்கங்கள், ஒப்பீடுகள்: உங்கள் நிலைக்கு ஏற்ப விளக்கம்.',
    vdModeHelp_quiz: 'ஒரு தலைப்பைச் சொல்லுங்கள்; தேர்வு முறைக் கேள்விகள் கிடைக்கும், உடனே மதிப்பிடப்படும். தவறான விடைகள் உங்கள் தவறுகள் தொகுப்பில் சேரும்.',
    vdModeHelp_visualize: 'எந்தத் தலைப்புக்கும் மன வரைபடங்கள், பாய்வுப் படங்கள், காலவரிசைகள், விளக்கப்படங்கள்.',
    vdDifficulty: 'கடினநிலை',
    vdExamStyle: 'தேர்வு முறை',
    vdLevel_mixed: 'கலவை',
    vdLevel_medium: 'நடுத்தரம்',
    vdSend: 'அனுப்பு',
    vdStop: 'நிறுத்து',
    vdThinking: 'Vidya யோசிக்கிறது…',
    vdSaveToNotebook: 'குறிப்பேட்டில் சேமி',
    vdSavedToNotebook: 'உங்கள் குறிப்பேட்டில் சேமிக்கப்பட்டது',
    vdCopy: 'நகலெடு',
    vdActivityEmpty: 'Vidya ஒரு விடையைத் தயாரிக்கும்போது ஒவ்வொரு படியும் இங்கே தோன்றும்: யோசித்தல், தேடுதல், வினாடி வினா தயாரித்தல், வரைதல்.',
    vdStarting: 'தொடங்குகிறது…',
    vdUsage: 'இன்றைய நியாயமான பயன்பாட்டு வரம்பில் {pct}% பயன்படுத்தப்பட்டது',
    vdTool_think: 'யோசிக்கிறது',
    vdTool_web_search: 'இணையத்தில் தேடுகிறது',
    vdTool_show_quiz: 'வினாடி வினா தயாரிக்கிறது',
    vdTool_show_flashcards: 'நினைவு அட்டைகள் தயாரிக்கிறது',
    vdTool_show_chart: 'விளக்கப்படம் வரைகிறது',
    vdTool_show_diagram: 'வரைபடம் வரைகிறது',
    vdTool_remember: 'உங்கள் சுயவிவரத்தைப் புதுப்பிக்கிறது',
    vdTool_get_my_mistakes: 'உங்கள் தவறுகள் தொகுப்பைப் பார்க்கிறது',
    vdState_error: 'தோல்வி',
    vdError_limit: 'மிக வேகமாகச் செய்திகளை அனுப்புகிறீர்கள். சற்றுக் காத்திருந்து மீண்டும் முயலுங்கள்.',
    vdError_allowance: 'இன்றைய நியாயமான பயன்பாட்டு வரம்பைப் பயன்படுத்திவிட்டீர்கள். நாளை மீண்டும் தொடங்கும்.',
    vdError_invalid: 'அந்தச் செய்தியை அனுப்ப முடியவில்லை. சுருக்கி மீண்டும் முயலுங்கள்.',
    vdYourAnswer: 'உங்கள் விடை',
    vdModelAnswer: 'மாதிரி விடை:',
    vdMarkYourself: 'சரியாகச் சொன்னீர்களா?',
    vdIGotIt: 'சரியாகச் சொன்னேன்',
    vdIMissedIt: 'தவறவிட்டேன்',
    vdAnswerWas: 'விடை: {answer}',
    vdMyAnswer: 'என் விடை: {given}',
    vdAnsweredN: '{total}-இல் {n} விடையளிக்கப்பட்டது',
    vdCheckAnswers: 'விடைகளைச் சரிபார்',
    vdScore: 'மதிப்பெண்: {score}/{total}',
    vdAddedToBank: '{n} உங்கள் தவறுகள் தொகுப்பில் சேர்க்கப்பட்டன',
    vdExplainMistakesPrompt: '{topic} பற்றிய வினாடி வினாவை எழுதினேன். இந்தக் கேள்விகளில் நான் எங்கே தவறினேன், எந்தக் கருத்துகளைத் திருப்புதல் செய்ய வேண்டும் என்று விளக்குங்கள்:',
    vdAnotherQuizPrompt: '{topic} பற்றி இன்னொரு வினாடி வினா தாருங்கள், சற்றுக் கடினமாக.',
    vdFlashcards: 'நினைவு அட்டைகள்',
    vdCardOf: '{n} / {total}',
    vdFlip: 'அட்டையைப் புரட்டு',
    vdPrev: 'முந்தையது',
    vdNext: 'அடுத்தது',
    vdShuffle: 'கலை',
    vdNotebookHelp: 'நீங்கள் சேமித்த விடைகள், தீர்வுகள், வரைபடங்கள்; எப்போது வேண்டுமானாலும் திருப்புதல் செய்யலாம்.',
    vdNoNotes: 'இன்னும் எதுவும் சேமிக்கப்படவில்லை. எந்த விடையின் கீழும் உள்ள "குறிப்பேட்டில் சேமி" என்பதைப் பயன்படுத்துங்கள்.',
    vdDeleteNoteConfirm: 'இந்தக் குறிப்பை நீக்கவா?',
    vdAskFollowUp: 'தொடர் கேள்வி கேளுங்கள்',
    vdAskAboutNote: 'என் குறிப்பு "{title}" பற்றி: இதை நன்கு புரிந்துகொள்ள உதவுங்கள்.',
    vdQuizMe: 'இதில் எனக்கு வினாடி வினா வை',
    vdQuizMeOnNote: 'என் குறிப்பு "{title}" பற்றி எனக்கு வினாடி வினா வையுங்கள்.',
    vdMistakeHelp: 'தவறாக விடையளித்த கேள்விகள் திருப்புதலுக்குத் திரும்ப வரும்: சரியாகச் சொன்னால் அடுத்த முறை தாமதமாக வரும், தவறினால் விரைவில் மீண்டும் வரும்.',
    vdDueToday: 'இன்று திருப்ப வேண்டியவை',
    vdOpenMistakes: 'கற்க வேண்டியவை',
    vdMastered: 'கற்றுத் தேர்ந்தவை',
    vdReviewDue: 'இப்போது {n} திருப்புதல் செய்',
    vdPracticeWeak: 'என் பலவீனமான தலைப்புகளில் பயிற்சி',
    vdPracticeWeakPrompt: 'என் பலவீனமான தலைப்புகளில் ஒரு திருப்புதல் வினாடி வினா தாருங்கள்: {topics}.',
    vdNoMistakes: 'இன்னும் தவறுகள் எதுவும் சேமிக்கப்படவில்லை. ஒரு வினாடி வினா எழுதுங்கள்; தவறான விடைகள் இங்கே தோன்றும்.',
    vdNextReview: 'அடுத்த திருப்புதல் {date}',
    vdRemove: 'நீக்கு',
    vdShowAnswer: 'விடையைக் காட்டு',
    vdYouAnsweredBefore: 'முன்பு நீங்கள் அளித்த விடை: {given}',
    vdReviewDone: 'திருப்புதல் முடிந்தது',
    vdReviewScore: '{total}-இல் {n} சரி.',
    vdBackToBank: 'தவறுகள் தொகுப்புக்குத் திரும்பு',
    vdProfileHelp: 'ஒவ்வொரு விடையையும் உங்களுக்கு ஏற்றபடி அமைக்க Vidya இதைப் பயன்படுத்துகிறது. நீங்களும் Vidya-வும் மட்டுமே இதைப் பார்க்க முடியும்.',
    vdStatQuizzes: 'எழுதிய வினாடி வினாக்கள்',
    vdStatDays: 'செயல்பட்ட நாட்கள்',
    vdLevelName_beginner: 'தொடக்க நிலை',
    vdLevelName_advanced: 'உயர் நிலை',
    vdStyle: 'விடை பாணி',
    vdStyle_concise: 'சுருக்கமாக, நேரடியாக',
    vdStyle_detailed: 'விரிவாக',
    vdAnswerLanguage: 'விடை மொழி',
    vdSiteLanguage: 'தளத்தின் மொழியே',
    vdSaved: 'சேமிக்கப்பட்டது',
    vdMemoryHelp: 'நீங்கள் பகிரும் நீடித்த விவரங்களை Vidya குறித்துக்கொள்ளும், எ.கா. இலக்குகள், கடினமாக உணரும் தலைப்புகள். வேண்டாதவற்றை நீக்கலாம்.',
    vdNoMemory: 'இன்னும் எதுவும் இல்லை. நீங்கள் உரையாடும்போது உங்கள் இலக்குகள் போன்றவற்றை Vidya நினைவில் வைக்கும்.',
    vdForget: 'மறந்துவிடு',
    vdFact_preference: 'விருப்பம்',
    vdFact_struggle: 'கடினமாக உணர்வது',
    vdFact_strength: 'வலுவானது',
    exExams: 'நாங்கள் பயிற்சி அளிக்கும் தேர்வுகள்',
    exExamsText: 'தமிழ்நாடு ஆசிரியர் தேர்வு மற்றும் தகுதித் தேர்வுகளுக்கான ஆன்லைன் பயிற்சி, மாதிரித் தேர்வுகள், படிப்புத் திட்டங்கள்.',
    exCoaching: '{exam} பயிற்சி',
    exPattern: '{exam} தேர்வு அமைப்பு',
    exSection: 'பகுதி',
    exQuestions: 'வினாக்கள்',
    exMarks: 'மதிப்பெண்',
    exTotal: '{m} நிமிடங்களில் {n} வினாக்கள்.',
    exTamilElig: 'தகுதிக்கான தமிழ்த் தேர்வும் இதில் அடங்கும்.',
    exSubjects: 'பாடங்கள்',
    exCourses: '{exam} பாடநெறிகள்',
    exNoCourses: 'இத்தேர்வுக்கான புதிய பாடநெறிகள் விரைவில் வருகின்றன.',
    exFree: 'இலவசம்',
    exMocks: 'Vidya AI உடன் {exam} மாதிரித் தேர்வுகள்',
    exMocksText: 'தேர்வு அமைப்பிலேயே, நேரக் கணக்குடனும் மதிப்பீட்டுடனும் முழு மாதிரித் தேர்வுகள்; நீங்கள் எங்கே மதிப்பெண் இழக்கிறீர்கள் என்ற ஆய்வுடன். ஒவ்வொரு கட்டணப் பாடநெறியுடனும் இலவசம்.',
    exFaq: 'கேள்விகள்',
    hdrDiscover: 'புதிய கண்டுபிடிப்பு',
    hdrMenu: 'மெனு',
  },
};


export function currentLanguage() {
  return (getLocale() || 'en').toLowerCase().split('-')[0];
}

export function useMessages() {
  const lang = currentLanguage();
  return { ...MESSAGES.en, ...(GENERATED[lang] || {}), ...(MESSAGES[lang] || {}) };
}

export function format(message, values) {
  return Object.entries(values || {}).reduce((s, [k, v]) => s.replace(`{${k}}`, v), message);
}

// Languages offered in the switcher: THERVUMITRA_LANGUAGES from the LMS
// MFE_CONFIG if set (codes, optionally with label/name/dir), else all of them.
export function getLanguages() {
  const configured = getConfig().THERVUMITRA_LANGUAGES;
  if (!Array.isArray(configured) || !configured.length) { return ALL_LANGUAGES; }
  return configured.map((entry) => {
    const code = typeof entry === 'string' ? entry : entry.code;
    const known = ALL_LANGUAGES.find(l => l.code === code) || { code, label: code, name: code };
    return { ...known, ...(typeof entry === 'string' ? {} : entry) };
  });
}

// Keep <html lang> and dir in step with the UI language: screen readers and the
// Indic line-height rules in the theme depend on it.
export function syncDocumentLanguage() {
  if (typeof document !== 'undefined') {
    const lang = currentLanguage();
    document.documentElement.lang = lang;
    document.documentElement.dir = RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
  }
}
